import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  Handle,
  Position,
  useNodesState,
  useReactFlow,
  useViewport,
  type Node,
  type Edge,
  type EdgeChange,
  MarkerType,
  type IsValidConnection,
  ConnectionMode,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { LineRowNode, StubMarkerNode, TILE_MENU_CLOSE_EVENT, type LineRowNodeData } from './nodes/LineRow';
import {
  buildLineSideMap,
  isManualLineEdge,
  isValidLineConnection,
  type LineSideEntry,
  type LineSideMap,
} from './lineLinkValidation';
import { BestellungNode, type BestellungNodeData } from './nodes/BestellungNode';
import { MatchedLineEdge } from './nodes/MatchedLineEdge';
import { MatchedLineEdgeActionsContext } from './nodes/matchedLineEdgeActions';
import { EdgeUnlinkMenu } from './nodes/EdgeUnlinkMenu';
import { PayoutStatementNode, type PayoutStatementNodeData } from './nodes/PayoutStatementNode';
import { PayoutAdviceNode, type PayoutAdviceNodeData } from './nodes/PayoutAdviceNode';
import { VerticalScrollbar } from './VerticalScrollbar';
import { buildHandleConnect, buildHandleConnectEnd } from './handleConnect';
import { postLineLinkRejection } from './linkRejectionTelemetry';
import { buildEdgeUnlinkHandlers, type PendingUnlink } from './edgeUnlinkHandlers';
import { postLineUnlinkTelemetry } from './lineUnlinkTelemetry';
import { DocumentRefEdge, type DocumentRefEdgeData } from './nodes/DocumentRefEdge';
import { DocumentRefEdgeActionsContext } from './nodes/documentRefEdgeActions';
import {
  buildDocumentUnlinkHandlers,
  type PendingDocumentUnlink,
} from './documentUnlinkHandlers';
import { isDocumentUnlinkEdge, resolveDocumentLinkKind } from './documentLinkValidation';
import {
  DOCUMENT_UNLINK_CONFIRM_TITLE,
  formatDocumentLabel,
} from './documentUnlinkConfirmation';
import {
  buildLineDescriptorMap,
  UNLINK_ACTION_LABEL,
  UNLINK_CONFIRM_CANCEL,
  UNLINK_CONFIRM_TITLE,
} from './unlinkConfirmation';
import ConfirmModal from '../../../components/ConfirmModal';
import { useToast } from '../../../contexts/ToastContext';
import type { MatchGroup, MatchGroupLine, MatchLineStatus } from '../../../hooks/useMatchGroups';
// #1289 — shared LS/GS accessors relocated to deliveryGroupSummary so
// the feed view derives identical figures. ``headerDateValue`` is
// re-exported below so the #839 unit test keeps importing from here.
import { eurFmt, formatDate, headerDateValue } from './deliveryGroupSummary';
import { buildPriceIndex, estimateOrphanDeliveryValue } from './priceIndex';
import { deliveryReferenceLabel, firstResolvableReference } from './deliveryReferences';

export { headerDateValue } from './deliveryGroupSummary';
import { useMeasuredHeights } from './useMeasuredHeights';
import {
  COLUMN_ORDER,
  moveSelection,
  type ColumnKey as NavColumnKey,
  type Grid as NavGrid,
  type GridRow as NavGridRow,
  type NavKey,
  type Selection as NavSelection,
  type TileRef as NavTileRef,
} from './keyboardNav';
import './nodes/LineRow.css';

// Constant: produce-line resource categories that get rendered as
// goods rows in LineRow (everything else folds into the footer chip
// row). Kept here in sync with LineRow.tsx so the orphan-stub height
// estimate matches the real tile rendered next to it.
const PRODUCE_CATEGORIES = new Set(['goods']);

// #1120 — bold-green preview style for the manual line-link drag.
// The default xyflow connection line is a thin dark bezier — operators
// couldn't tell what they were connecting mid-drag. Matches the
// optimistic-edge stroke colour (#10b981, the emerald already used on
// the post-drop optimistic edge) so the preview visually merges into
// the optimistic edge on drop (no colour pop). strokeWidth = 3 reads
// as "bold" against the canvas's 1.0–1.5 dashed reference / Abrechnung
// / stub edges; higher values start obscuring the connect-spot dots
// near the source. Exported so the unit test can pin the style
// without mounting ReactFlow.
export const MANUAL_LINE_LINK_CONNECTION_STYLE: CSSProperties = {
  stroke: '#10b981',
  strokeWidth: 3,
};

function SectionBannerNode({ data }: { data: { label: string; width: number } }) {
  return (
    <div
      style={{
        width: data.width,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        color: '#6b7280',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        userSelect: 'none',
      }}
    >
      <span style={{ flex: '0 0 auto' }}>{data.label}</span>
      <span style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
    </div>
  );
}

export interface MissingDocNodeData {
  /** Which side the MISSING document would have been on — drives the
   *  primary label. ``bestellung`` placeholder is the leftmost column
   *  (sales_agreement cited by an LS/GS but never ingested as its own
   *  document — #832); ``delivery`` is the middle LS column;
   *  ``settlement`` is the right GS column; ``abrechnung`` is the
   *  phase-4 payout-statement column. */
  missingSide: 'bestellung' | 'delivery' | 'settlement' | 'abrechnung';
  width: number;
  height: number;
  /** Optional money hint, e.g. "ca. 12.300 € erwartet" for an orphan
   *  delivery (price estimated from same-family paired evidence) or
   *  "Gutschriftbetrag 8.420 €" for an orphan settlement (the credit
   *  value sitting without delivery proof). #832 — renders into the
   *  subtitle alongside ``expectedNumber`` (joined with ``·``). */
  moneyHint?: string;
  /** Typed missing-doc placeholders — the BestellNr / LF-Nr / POS the
   *  upstream doc names. Pre-#832 this was the header ("Lieferschein
   *  #LF20250222 · (fehlt)"); post-#832 the title is the plain
   *  ``<DocType> fehlt`` and the typed key moves to the subtitle so
   *  the same key never appears twice on one tile (the
   *  missing-Bestellung "Bestellung #1273425 ... 1273425" duplication
   *  bug). ``null`` keeps an empty subtitle (or money-hint only). */
  expectedNumber?: string | null;
  /** Subtitle prefix discriminator for ``expectedNumber``.
   *  ``docNumber`` (default) renders ``#<expectedNumber>`` —
   *  the BestellNr / LF-Nr shape. ``posToken`` renders
   *  ``POS <expectedNumber>`` — the GS-via-POS shape from #793
   *  phase 2 (the token IS the POS reference, not a doc id). */
  expectedNumberKind?: 'docNumber' | 'posToken';
  /** #844 — keyboard-focus ring driver (see LineRowNodeData.isFocused).
   *  Renders the same 2px indigo halo every other tile uses so the
   *  ring reads as a uniform affordance across the canvas. */
  isFocused?: boolean;
}

/**
 * Shared placeholder tile across all four canvas columns (Bestellung /
 * Lieferschein / Gutschrift / Abrechnung). Dashed border + muted
 * surface keeps it visually subordinate to real tiles while still
 * occupying the column position so the eye can locate the absent doc.
 *
 * #832 — Title is always ``<DocType> fehlt`` (plain, no typed key
 * baked in). Typed key (the BestellNr / LF-Nr / POS the upstream doc
 * names) goes to the subtitle alongside any ``moneyHint`` — joined
 * with ``·`` — so the same identifier never appears twice on one
 * tile. Pre-#832 the typed key lived in the header
 * (``Lieferschein #LF20250222 · (fehlt)``), which read as duplication
 * on the Bestellung column where the BestellNr was already on the
 * right-aligned secondary slot of the real-tile path (the
 * "Bestellung #1273425 ... 1273425" screenshot from the bug report).
 *
 * Exported so Vitest unit tests pin the title/subtitle contract
 * directly. Otherwise consumed only by ``MatchGraph``'s ``nodeTypes``
 * registry below.
 */
export function MissingDocNode({ data }: { data: MissingDocNodeData }) {
  const docTypeLabel =
    data.missingSide === 'bestellung'
      ? 'Bestellung'
      : data.missingSide === 'delivery'
        ? 'Lieferschein'
        : data.missingSide === 'abrechnung'
          ? 'Abrechnung'
          : 'Gutschrift';
  const title = `${docTypeLabel} fehlt`;
  // #832 — subtitle composes (typed key) · (money hint), omitting
  // whichever is absent. Both absent → no subtitle line at all
  // (preserves the original "Gutschrift fehlt"-only rendering when the
  // upstream doc carries no signal — Acceptance §A.4: no signal → no
  // key).
  const numberPrefix = data.expectedNumberKind === 'posToken' ? 'POS ' : '#';
  const expectedKeyText =
    data.expectedNumber != null && data.expectedNumber.length > 0
      ? `${numberPrefix}${data.expectedNumber}`
      : null;
  const subtitleParts: string[] = [];
  if (expectedKeyText) subtitleParts.push(expectedKeyText);
  if (data.moneyHint && data.moneyHint.length > 0) subtitleParts.push(data.moneyHint);
  const subtitle = subtitleParts.length > 0 ? subtitleParts.join(' · ') : null;
  // Title-target placement follows the inside-edge rule: the handle
  // faces the *real* tile so the arrowhead lands on the inside edge
  // of the placeholder.
  //   - missingSide 'bestellung' → placeholder in LEFTMOST column,
  //     real GS/LS to its right → handle on RIGHT (#832).
  //   - missingSide 'delivery'   → real GS to its right → handle on
  //     RIGHT (#793 phase 1).
  //   - missingSide 'settlement' → real LS to its left → handle on
  //     LEFT (#793 phase 2).
  //   - missingSide 'abrechnung' → real GS to its left → handle on
  //     LEFT (#793 phase 4).
  // Vertical position rules:
  // - ``bestellung`` + ``delivery`` anchor on the RIGHT edge at top:
  //   44 (paired with ``referenz-source`` on the GS left edge at top:
  //   44 — the horizontal-arrow invariant established by the
  //   ``REF_ROW_PUSH = 22`` cancellation).
  // - ``abrechnung`` anchors on the LEFT edge at top: 22 (#1119 case
  //   2). Source-side GS handle is ``title-source`` at top: 22 (added
  //   by #902 + re-anchored from ``referenz-source`` by #1067); the
  //   pre-#1119 placeholder anchor at top: 44 was a regression vs the
  //   #1067 source move and sloped the arrow by 22 px.
  // - ``settlement`` anchors on the LEFT edge at top: 50% so the
  //   multi-line POS fan-in reads as a fan rather than a stack.
  const isRightHandlePlaceholder =
    data.missingSide === 'delivery' || data.missingSide === 'bestellung';
  const isAbrechnungPlaceholder = data.missingSide === 'abrechnung';
  return (
    <div
      style={{
        width: data.width,
        height: data.height,
        border: '1.5px dashed #d1d5db',
        borderRadius: '0.5rem',
        background:
          'repeating-linear-gradient(45deg, #fafafa, #fafafa 8px, #f3f4f6 8px, #f3f4f6 16px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        userSelect: 'none',
        boxSizing: 'border-box',
        padding: '0.75rem',
        position: 'relative',
        // #844 — keyboard-focus ring. ``box-shadow`` rather than
        // ``outline`` so the ring composes cleanly on top of the
        // dashed border (no outline-vs-border z-fighting) and uses
        // the same indigo halo as the .line-row-node--focused CSS
        // rule does on real tiles, keeping the affordance uniform
        // across the 5 tile types.
        ...(data.isFocused
          ? { boxShadow: '0 0 0 2px #6366f1, 0 1px 3px rgba(0, 0, 0, 0.06)' }
          : {}),
      }}
    >
      <Handle
        type="target"
        position={isRightHandlePlaceholder ? Position.Right : Position.Left}
        id="title-target"
        isConnectable={false}
        style={{
          position: 'absolute',
          ...(isRightHandlePlaceholder
            ? { top: 44, right: -4 }
            : isAbrechnungPlaceholder
              ? { top: 22, left: -4 }
              : { top: '50%', left: -4, transform: 'translateY(-50%)' }),
          width: 6,
          height: 6,
          background: 'transparent',
          border: 'none',
          opacity: 0,
        }}
      />
      {/* #915 — sparse Bestellung placeholders may receive an LS→Bestellung
          edge whose ``pickBestellungSourceCard`` resolution targets the
          left handle (LS shares the COL_LEFT x position post-collapse;
          smoothstep routes the dashed edge AROUND the left side of the
          column via Left→Left handles, avoiding intervening tiles in
          multi-Bestellung / multi-LS stacks — codex round 4 P2).
          Mirrors the real ``BestellungNode`` target shape so React Flow
          doesn't silently drop the edge when the Bestellung tile happens
          to be a placeholder. Other ``missingSide`` variants are
          unaffected — only the Bestellung-target edge ever resolves to
          ``title-target-left``. */}
      {data.missingSide === 'bestellung' && (
        <Handle
          type="target"
          position={Position.Left}
          id="title-target-left"
          isConnectable={false}
          style={{
            position: 'absolute',
            top: 44,
            left: -4,
            width: 6,
            height: 6,
            background: 'transparent',
            border: 'none',
            opacity: 0,
          }}
        />
      )}
      <div
        style={{
          fontWeight: 600,
          color: '#92400e',
          fontSize: 14,
          letterSpacing: 0.2,
          textAlign: 'center',
        }}
      >
        {title}
      </div>
      {subtitle && (
        <div style={{ fontSize: 12, color: '#6b7280', fontVariantNumeric: 'tabular-nums' }}>
          {subtitle}
        </div>
      )}
    </div>
  );
}

const nodeTypes = {
  lineRow: LineRowNode,
  stubMarker: StubMarkerNode,
  sectionBanner: SectionBannerNode,
  missingDoc: MissingDocNode,
  bestellung: BestellungNode,
  payoutStatement: PayoutStatementNode,
  payoutAdvice: PayoutAdviceNode,
};

// #1344 — custom edge types. ``matchedLine`` renders the green/amber
// line↔line match edge with a ``selected``-aware stroke + a wide
// invisible interaction hit target, so an operator clicking the edge
// sees it arm and can Backspace/Delete to unlink. Dashed reference /
// Abrechnung / stub edges keep the builtin ``smoothstep`` / ``straight``
// types and stay unselectable for delete (``isManualLineEdge`` already
// scopes ``handleEdgesDelete`` to the line↔line edges).
// HOF-219 — ``documentRef`` renders the dotted grey GS→LS DOCUMENT edge with
// the same selection-aware treatment ``matchedLine`` gets, plus the on-edge
// ``Verknüpfung lösen`` control. The Abrechnung, Zahlungsavis, placeholder and
// stub arrows keep the builtin types and stay inert — there is no unlink for
// those at any layer, and giving them a control would promise one.
const edgeTypes = {
  matchedLine: MatchedLineEdge,
  documentRef: DocumentRefEdge,
};

/**
 * #843 — unified arrow primitive shared by every "dotted grey
 * reference" edge on the canvas: GS→LS REF (resolved + orphan-LS
 * placeholder), LS→GS POS-token placeholder (#793 phase 2). Pre-#843,
 * the LS→GS edge rendered as a straight diagonal (`type: 'straight'`)
 * while the GS→LS REF used an orthogonal-elbow path (`smoothstep` with
 * rounded corners). Now both directions call this single builder, so
 * future canvas-style changes touch one code path.
 *
 * Routing modes mirror the GS→LS REF logic (MatchGraph.tsx ~L1170):
 *   - `coLinear: true`  → `straight`. When the source + target handles
 *     sit at the same canvas y (e.g. orphan_settlement: placeholder IS
 *     the LS card, or paired cardIndex 0 → cardIndex 0 after
 *     `REF_ROW_PUSH`), a curved router would draw an unneeded S-bend
 *     through co-linear endpoints.
 *   - default (`coLinear: false`) → `smoothstep` with `borderRadius:
 *     12` and `offset: 20` — half the 40-px column gap so the bend
 *     points on both sides land at the same x and the Z-router
 *     collapses into a clean single-segment L.
 *
 * Visual style (dash + stroke + arrowhead) is fixed by the canvas
 * design and not configurable per call-site — callers pass only the
 * graph identity (id, source/target node + handle ids) and the
 * routing flag.
 */
/**
 * #1122 — smoothstep bend offset for every routed reference edge on
 * the Gutschrift-Check canvas. Adjacent columns are 40 px apart
 * (``COL_RIGHT − (COL_LEFT + TILE_WIDTH) = 40``). The pre-#1122 value
 * of 20 (exactly half the column gap) put bend points on both sides
 * of the gap at the same x = column-gap midpoint — geometrically
 * clean for the GS→LS single-source REF case, but after #1048 split
 * the two GS-leaving left-bound source handles (``referenz-source``
 * at top: 44, ``bestellung-from-gs`` at top: 22) the bend curves at
 * the midpoint sat 22 px apart vertically and overlapped visually
 * around the corner — the "twisted arrows" symptom reported in #1122.
 *
 * Halving the offset to 10 moves the bend points to x = 410 (source
 * side) and x = 430 (target side), 20 px apart, leaving each
 * GS-leaving curve room to traverse ``handle.y → target.y`` without
 * crowding the sibling.
 *
 * Both ``buildDashedReferenceEdge`` AND the inline payout_advice
 * edge in ``buildPayoutColumns`` must use the same offset — they
 * route across the same column gaps. The constant is exported so
 * future edits keep the two emission sites in lockstep (and so the
 * #1122 unit test can pin both at the same value).
 */
export const DASHED_EDGE_OFFSET = 10;

/**
 * #1174 — MIRRORED smoothstep geometry for the two GS-LEAVING dashed
 * reference arrows so they route AROUND each other instead of crossing.
 *
 * Lineage: #1048 split the two GS-leaving sources onto distinct handles
 * (``referenz-source`` top: 44 → LS REF, ``bestellung-from-gs`` top: 22
 * → Bestellung). #1122/#1159 then tried to separate their VERTICAL legs
 * by tuning the smoothstep ``offset`` (10 vs 24). That never worked: for
 * two handles that FACE each other across the column gap (GS left edge
 * Position.Left at x = COL_RIGHT = 440 → target right edge Position.Right
 * at x = COL_LEFT + TILE_WIDTH = 400), xyflow's ``getSmoothStepPath``
 * places the vertical-leg trunk at ``centerX = sourceGapped.x +
 * (targetGapped.x − sourceGapped.x) · stepPosition``. With the default
 * ``stepPosition = 0.5`` the ``offset`` term CANCELS — centerX collapses
 * to the gap midpoint (440 − offset + 400 + offset) / 2 = 420 — so BOTH
 * arrows' trunks landed at x = 420 regardless of the 10-vs-24 offset, and
 * they still crossed near the GS edge (the residual #1174 reports).
 *
 * The real lever is ``stepPosition`` (threaded through to React Flow's
 * SmoothStepEdge), which slides the trunk along the gap. We mirror it:
 *
 *   - GS_LEAVING_EDGE_OFFSET = 8 widens the trunk band to
 *     [400 + 8, 440 − 8] = [408, 432] (24 px) so stepPosition has room
 *     to actually move the trunk off-midpoint.
 *   - The UPPER-anchored arrow (Bestellung, source top: 22, target is the
 *     Bestellung tile ABOVE the LS) bends EARLY/RIGHT (near GS):
 *     GS_BESTELLUNG_STEP_POSITION = 0.18 → trunk ≈ x 428. It peels UP off
 *     the gap immediately and runs across the TOP.
 *   - The LOWER-anchored arrow (REF, source top: 44, target is the LS
 *     BELOW) bends LATE/LEFT (near LS): GS_REF_STEP_POSITION = 0.82 →
 *     trunk ≈ x 412. It stays LOW across the gap, then drops to the LS.
 *
 * Result: higher-anchored arrow bends higher and sooner, lower one bends
 * lower and later; the two trunks sit ~16 px apart on OPPOSITE sides of
 * the midpoint and travel in opposite y directions — they route around
 * each other, no intersection. Other dashed refs + the green matched edge
 * keep DASHED_EDGE_OFFSET / default stepPosition and are untouched.
 */
export const GS_LEAVING_EDGE_OFFSET = 8;
export const GS_BESTELLUNG_STEP_POSITION = 0.18;
export const GS_REF_STEP_POSITION = 0.82;

export function buildDashedReferenceEdge(params: {
  id: string;
  source: string;
  sourceHandle: string;
  target: string;
  targetHandle: string;
  coLinear?: boolean;
  /** Override the smoothstep bend offset (#1174 GS-leaving mirror). */
  offset?: number;
  /**
   * Override the smoothstep trunk position along the column gap (#1174).
   * 0 = bend at the source edge, 1 = bend at the target edge, 0.5
   * (default) = gap midpoint. Mirrored on the two GS-leaving arrows so
   * their verticals fall on opposite sides and don't cross.
   */
  stepPosition?: number;
}): Edge {
  const coLinear = params.coLinear === true;
  const offset = params.offset ?? DASHED_EDGE_OFFSET;
  return {
    id: params.id,
    source: params.source,
    sourceHandle: params.sourceHandle,
    target: params.target,
    targetHandle: params.targetHandle,
    type: coLinear ? 'straight' : 'smoothstep',
    ...(coLinear
      ? {}
      : {
          pathOptions: {
            borderRadius: 12,
            offset,
            // Only emit stepPosition when overridden so existing edges
            // (and their snapshots) keep React Flow's 0.5 default.
            ...(params.stepPosition != null ? { stepPosition: params.stepPosition } : {}),
          },
        }),
    style: { stroke: '#9ca3af', strokeWidth: 1, strokeDasharray: '4 3' },
    markerEnd: { type: MarkerType.Arrow, color: '#9ca3af', width: 14, height: 14 },
  };
}

/**
 * HOF-219 — the GS→LS DOCUMENT reference edge, as a severable one.
 *
 * Wraps ``buildDashedReferenceEdge`` rather than replacing it: the geometry,
 * the dash, the marker and the #1174 bend overrides all stay exactly where they
 * were, and this only re-types the edge and stamps the identity the unlink
 * needs. Every OTHER caller of the dashed primitive (Abrechnung, Zahlungsavis,
 * the missing-doc placeholders, the stubs) is untouched and stays inert, which
 * is what keeps ``isDocumentUnlinkEdge`` from ever matching one of them.
 *
 * The labels are resolved HERE, at emission, rather than looked up when the
 * confirmation opens: a canvas refetch can land between the right-click and the
 * confirm, and a dialog that names the wrong Lieferschein is worse than one
 * built from a slightly stale label.
 */
export function buildDocumentRefEdge(params: {
  id: string;
  source: string;
  sourceHandle: string;
  target: string;
  targetHandle: string;
  coLinear?: boolean;
  offset?: number;
  stepPosition?: number;
  settlementEvent: Record<string, unknown>;
  deliveryEvent: Record<string, unknown>;
  groupId: string;
  linkKind: string | null;
}): Edge {
  const base = buildDashedReferenceEdge(params);
  const settlementEventId = String(params.settlementEvent.entity_id ?? '');
  const deliveryEventId = String(params.deliveryEvent.entity_id ?? '');
  return {
    ...base,
    type: 'documentRef',
    data: {
      coLinear: params.coLinear === true,
      // Carried on ``data`` because the custom component picks its own builtin
      // renderer and needs the bend options the primitive computed.
      pathOptions: (base as { pathOptions?: Record<string, unknown> }).pathOptions,
      settlementEventId,
      deliveryEventId,
      linkKind: params.linkKind,
      groupId: params.groupId,
      settlementLabel: formatDocumentLabel('settlement', params.settlementEvent),
      deliveryLabel: formatDocumentLabel('delivery', params.deliveryEvent),
    } satisfies DocumentRefEdgeData,
  };
}

/**
 * #1159 — line-level match edge (LS line ↔ GS line). A true ``matched`` pair now
 * uses the same cornered ``smoothstep`` geometry as the dotted reference edges
 * (rounded right-angle corners) instead of a straight diagonal, while keeping its
 * SOLID green stroke (``#10b981``, no dasharray) so it stays visually distinct
 * from the grey dotted references — only the path geometry matches. Non-``matched``
 * near-match edges (``#f59e0b``) keep the straight diagonal, so they don't regress.
 */
export function buildMatchedLineEdge(params: {
  id: string;
  source: string;
  sourceHandle: string;
  target: string;
  targetHandle: string;
  matched: boolean;
}): Edge {
  const stroke = params.matched ? '#10b981' : '#f59e0b';
  // #1344 — render through the ``matchedLine`` custom edge type so the
  // stroke reacts to React Flow's ``selected`` prop. An inline
  // ``style.stroke`` wins the CSS cascade over ``.react-flow__edge.selected``,
  // so a selected matched edge used to look identical to an unselected one
  // and operators never trusted Backspace/Delete to unlink. The geometry +
  // base colour move onto ``data``; ``MatchedLineEdge`` re-renders the SAME
  // builtin smoothstep (matched, cornered) / straight (near-match, diagonal)
  // edge with the same ``pathOptions``, so #1159 / #1174 routing is
  // unchanged. ``style`` keeps the base colour as a defensive default for
  // any render path that bypasses the custom component (e.g. the optimistic
  // drag edge before the custom type is registered).
  const pathOptions = params.matched
    ? { borderRadius: 12, offset: DASHED_EDGE_OFFSET }
    : undefined;
  return {
    id: params.id,
    source: params.source,
    sourceHandle: params.sourceHandle,
    target: params.target,
    targetHandle: params.targetHandle,
    type: 'matchedLine',
    data: { matched: params.matched, stroke, ...(pathOptions ? { pathOptions } : {}) },
    style: { stroke, strokeWidth: 1.25 },
  };
}

/**
 * #1291 N:1 fan-out — emit one matched/near-match edge PER PAIR of a
 * delivery line (``e_${deliveryLineId}_${settlementLineId}``, the same
 * per-pair-unique id shape the optimistic edge + dedupe already use).
 * All fan-out edges share the line's single source handle (xyflow
 * accepts multiple edges per handle) and carry the line's AGGREGATE
 * status colour, so a reconciled multi-pair line renders all-green and
 * a defect line renders all-amber — one coherent verdict per LS spot.
 *
 * ``needsStub`` preserves the pre-#1291 dashed-defect-stub rule,
 * generalised: a ``quantity_mismatch`` line gets the stub only when
 * ZERO edges could be drawn (no counterpart visible). Plain unmatched
 * lines stay quiet — the absence of an edge is the signal.
 *
 * Pure + exported so the fan-out contract is unit-testable without
 * mounting ReactFlow (same pattern as the neighbouring builders).
 */
export function buildDeliveryLineFanOutEdges(params: {
  dl: Pick<MatchGroupLine, 'lineId' | 'matchStatus' | 'matchedLineIds'>;
  sourceNodeId: string;
  seLineCoord: ReadonlyMap<string, { nodeId: string }>;
}): { edges: Edge[]; needsStub: boolean } {
  const { dl, sourceNodeId, seLineCoord } = params;
  const edges: Edge[] = [];
  for (const sid of dl.matchedLineIds) {
    const seCoord = seLineCoord.get(sid);
    if (!seCoord) continue;
    edges.push(
      buildMatchedLineEdge({
        id: `e_${dl.lineId}_${sid}`,
        source: sourceNodeId,
        sourceHandle: dl.lineId,
        target: seCoord.nodeId,
        targetHandle: sid,
        matched: dl.matchStatus === 'matched',
      }),
    );
  }
  return {
    edges,
    needsStub: edges.length === 0 && dl.matchStatus === 'quantity_mismatch',
  };
}

/**
 * #793 phase 2 — build placeholder GS tiles + per-line dotted POS
 * arrows for an orphan_delivery group whose LS carries unresolved
 * ``referenced_settlement_pos`` tokens. Pure: takes the group, the
 * column geometry, and the LS node id; returns the React Flow nodes
 * and edges to be appended. Extracted so the layout-pass logic is
 * unit-testable without mounting the whole canvas.
 *
 * - One placeholder per unique token (deduplicated upstream by the
 *   materialiser into ``expectedSettlementPosTokens``).
 * - #1160 — each placeholder is a FIXED-height compact tile
 *   (``BESTELLUNG_TILE_HEIGHT``, the same "compact standard height" the
 *   generic ``Gutschrift fehlt`` / ``Lieferschein fehlt`` stubs adopted
 *   in #1137), stacked with ``POS_TOKEN_GAP``. Previously each tile was
 *   sized by even-splitting the paired LS card height (``stubH``), so a
 *   1-token row rendered one full-card-height tile while a 4-token row
 *   on a short card clamped to 60px each — visibly inconsistent
 *   row-to-row. The returned ``stackHeight`` lets the caller grow the
 *   row envelope (``orphanRowH``) so a tall token stack never overlaps
 *   the next row.
 * - Per-LS-line arrows: each delivery line whose normalised
 *   ``referencedSettlementPos`` matches a token contributes one edge
 *   from the LS card's ``lineId`` source handle to the matching
 *   placeholder's ``title-target`` handle. Same dash + marker as the
 *   phase 1 GS→LS-placeholder edge for visual continuity.
 * - Money hint attaches to the FIRST placeholder only (the estimate
 *   is component-scoped, not per-token; duplicating it would mislead
 *   the operator into reading per-tile values).
 *
 * Returns ``{ nodes: [], edges: [] }`` when the token list is empty —
 * callers fall through to the generic single ``Gutschrift fehlt``
 * placeholder (phase 1's no-signal case from Acceptance §A.4).
 */
export function buildOrphanDeliveryPosPlaceholders(
  group: MatchGroup,
  options: {
    lsNodeId: string;
    x: number;
    yOffset: number;
    tileWidth: number;
    moneyHint?: string;
  },
): { nodes: Node[]; edges: Edge[]; stackHeight: number } {
  const tokens = group.expectedSettlementPosTokens;
  if (tokens.length === 0) return { nodes: [], edges: [], stackHeight: 0 };
  // #1160 — fixed-height tiles (no longer derived from the paired LS
  // card via ``stubH``). ``stackHeight`` is returned so the caller can
  // fold it into ``orphanRowH`` and reserve enough vertical space.
  const tokenH = BESTELLUNG_TILE_HEIGHT;

  const nodes: Node[] = [];
  const edges: Edge[] = [];
  tokens.forEach((token, idx) => {
    const tokenNodeId = `miss_gs_${group.id}__${token}`;
    nodes.push({
      id: tokenNodeId,
      type: 'missingDoc',
      position: { x: options.x, y: options.yOffset + idx * (tokenH + POS_TOKEN_GAP) },
      data: {
        missingSide: 'settlement',
        width: options.tileWidth,
        height: tokenH,
        moneyHint: idx === 0 ? options.moneyHint : undefined,
        expectedNumber: token,
        expectedNumberKind: 'posToken',
      } satisfies MissingDocNodeData,
      draggable: false,
      selectable: false,
    });
    if (!options.lsNodeId) return;
    for (const line of group.deliveryLines) {
      if (line.referencedSettlementPos !== token) continue;
      // #843 — orthogonal-elbow routing via the shared primitive. The
      // LS line handle and the placeholder's `title-target` handle are
      // almost never at the same canvas y (line handles are centred on
      // each line row, the placeholder anchors at top: 50%), so the
      // edge is non-co-linear by default. Smoothstep with offset: 20
      // collapses into a clean single-bend L across the 40-px LS→GS
      // column gap.
      edges.push(
        buildDashedReferenceEdge({
          id: `pos_miss_${group.id}_${line.lineId}_${token}`,
          source: options.lsNodeId,
          sourceHandle: line.lineId,
          target: tokenNodeId,
          targetHandle: 'title-target',
        }),
      );
    }
  });
  const stackHeight =
    tokens.length * tokenH + Math.max(0, tokens.length - 1) * POS_TOKEN_GAP;
  return { nodes, edges, stackHeight };
}

/**
 * #1119 case 1 — no-token fallback for orphan_delivery groups.
 *
 * When the LS lines carry zero ``expectedSettlementPosTokens`` (the
 * BFS couldn't ground any POS reference on a real settlement event),
 * the canvas previously emitted a generic ``Gutschrift fehlt``
 * placeholder in the GS column **with no edge** linking it back to
 * the real LS. The operator saw a floating placeholder next to the
 * LS card with no visual cue that the two belonged to the same
 * group.
 *
 * The mirror branch (``orphan_settlement``, real GS + missing LS)
 * DOES emit a dashed reference arrow from the GS to the missing-LS
 * placeholder (see the ``ref_miss_*`` edge at the orphan_settlement
 * site below). #1119 case 1 restores that symmetry in the opposite
 * direction: a dashed LS → miss_gs edge.
 *
 * Source = the real LS card's new ``title-source`` handle on its
 * right edge (top: 22) — added in lockstep on ``LineRow.tsx``. The
 * pre-existing ``title-target`` on the same edge can't double as a
 * source (target-typed) and ``referenz-source`` is GS-only (gated
 * ``!isLeft``). Target = the placeholder's ``title-target`` on its
 * left edge (anchored at top: 50% for ``missingSide: 'settlement'``).
 *
 * ``coLinear`` defaults to false — in multi-line LS cases the source
 * y (LS top + 22) and target y (placeholder top + 50%) won't
 * coincide, so smoothstep with the shared ``DASHED_EDGE_OFFSET``
 * (#1122 → 10) keeps the bend off the LS stack's right edge.
 *
 * Extracted as a discrete helper (rather than inlined into
 * ``MatchGraph.tsx``'s no-token branch) so the edge-presence + handle
 * wiring can be asserted by the same kind of focused unit test that
 * pins ``buildOrphanDeliveryPosPlaceholders``.
 */
export function buildOrphanDeliveryMissGsPlaceholder(
  group: MatchGroup,
  options: {
    lsNodeId: string;
    x: number;
    yOffset: number;
    tileWidth: number;
    moneyHint: string | undefined;
  },
): { node: Node; edge: Edge } {
  const missGsId = `miss_gs_${group.id}`;
  const node: Node = {
    id: missGsId,
    type: 'missingDoc',
    position: { x: options.x, y: options.yOffset },
    data: {
      missingSide: 'settlement',
      width: options.tileWidth,
      // #1137 — compact standard height (like "Abrechnung fehlt"),
      // NOT the paired LS card height. Decoupled from the row's
      // vertical advance: the caller reserves ``orphanRowH`` off the
      // real LS card's measured height so the next row never overlaps;
      // only this placeholder tile shrinks.
      height: BESTELLUNG_TILE_HEIGHT,
      moneyHint: options.moneyHint,
      expectedNumber: null,
    } satisfies MissingDocNodeData,
    draggable: false,
    selectable: false,
  };
  const edge = buildDashedReferenceEdge({
    id: `${missGsId}_edge`,
    source: options.lsNodeId,
    sourceHandle: 'title-source',
    target: missGsId,
    targetHandle: 'title-target',
  });
  return { node, edge };
}

/**
 * The LS number the orphan_settlement GS letterhead cites in its
 * ``delivery_reference`` attribute (same attribute #781's Ref chip
 * reads), or ``null`` when the GS names none. Single source for both
 * the miss_ls placeholder header and the GS → miss_ls reference-edge
 * gate, so the arrow is drawn iff the placeholder shows a number.
 */
function citedDeliveryReference(group: MatchGroup): string | null {
  // #3030 — shape-neutral. A wibill-native GS cites the whole SET of Lieferscheine
  // its invoice settles, so the value may be an array; all of them are shown,
  // because on an orphan settlement this string is the operator's only lead for
  // finding the missing document.
  return deliveryReferenceLabel(group.settlementEvent);
}

/**
 * #1137 — ``orphan_settlement`` "Lieferschein fehlt" placeholder (real
 * GS, missing LS). Renders at the compact ``BESTELLUNG_TILE_HEIGHT``
 * (like "Abrechnung fehlt"), decoupled from the paired GS card height
 * it previously inherited via ``stubH``. The caller still reserves the
 * row's vertical advance (``orphanRowH``) off the real GS card's
 * measured height (+ any payout-stack growth) so the next row never
 * overlaps — only this tile shrinks.
 *
 * Header promotes the GS letterhead's ``delivery_reference`` (the LS
 * number the GS cites — same attribute #781's Ref chip reads on the
 * live GS tile) so the operator sees "Lieferschein #LF…" instead of
 * the generic "Lieferschein fehlt" (#793 phase 1).
 *
 * Node-only: the GS → miss_ls dashed reference edge stays inline at the
 * orphan_settlement emission site (it is interleaved with the payout /
 * Bestellung wiring). Extracted so the compact-height + header contract
 * is unit-testable without mounting the full canvas — mirrors the
 * ``buildOrphanDeliveryMissGsPlaceholder`` extraction.
 */
export function buildOrphanSettlementMissLsPlaceholderNode(
  group: MatchGroup,
  options: {
    x: number;
    yOffset: number;
    tileWidth: number;
    moneyHint: string | undefined;
  },
): Node {
  return {
    id: `miss_ls_${group.id}`,
    type: 'missingDoc',
    position: { x: options.x, y: options.yOffset },
    data: {
      missingSide: 'delivery',
      width: options.tileWidth,
      height: BESTELLUNG_TILE_HEIGHT,
      moneyHint: options.moneyHint,
      expectedNumber: citedDeliveryReference(group),
    } satisfies MissingDocNodeData,
    draggable: false,
    selectable: false,
  };
}

/**
 * #793 phase 4 — emit the ABRECHNUNG (column 4) + ZAHLUNGSAVIS
 * (column 5) tiles plus their incoming arrows from one or more
 * source GS cards.
 *
 * Extracted into a pure helper so both the paired-groups loop AND the
 * orphan_settlement branch can call it without duplicating ~80 lines.
 * The orphan_delivery branch deliberately doesn't call this: orphan
 * LS components have no GS → no ``abrechnungs_number`` to cite, no
 * ``credit_note_number`` to be referenced.
 *
 * Returns the emitted nodes + edges plus the bottom Y of the tallest
 * payout-column stack so the caller can fold it into ``groupBottom`` /
 * ``rowH`` accounting.
 *
 * The helper is closed over the COL_* + tile-geometry constants
 * defined in this module (file-local on purpose — the constants are
 * coupled to MatchGraph's column layout, not portable). Callers pass
 * the source GS cards (lex-smallest seCards) so the Abrechnung
 * placeholder + Zahlungsavis edges anchor to the correct GS node.
 */
/**
 * #888 — Pick the source card + handle for the dashed BESTELLUNG arrow.
 *
 * Preference order (extracted from the inline closure for testability):
 *   1. LS card whose ``bestell_number`` matches the BestellNr — this
 *      is the only LS predicate that GUARANTEES LineRow renders the
 *      ``bestellung-source`` handle (see LineRow.tsx:277-284 — the
 *      handle is gated on ``isLeft && d.bestellNumber``). Pre-#888 a
 *      second LS-by-``agreement_id`` fallback existed here, but that
 *      branch could match an LS card whose ``bestell_number`` was
 *      empty/missing → the edge then targeted a handle that the DOM
 *      never rendered → React Flow dropped it silently and the
 *      BESTELLUNG tile looked floating with no arrow.
 *   2. (REMOVED in #888 — was "LS by agreement_id".)
 *   3. GS card whose ``agreement_id`` matches the BESTELLUNG entity —
 *      sources the ``bestellung-from-gs`` handle, which (post-#1048) is
 *      always rendered on GS tiles regardless of ``referenceTo``.
 *   4. Lex-smallest seCards[0] as the final defensive fallback for
 *      mid-pipeline runs where ``agreement_id`` hasn't propagated.
 *
 * #1048 — GS-source branch uses ``bestellung-from-gs`` (left edge,
 * top: 22), not ``referenz-source`` (left edge, top: 44). The
 * GS→LS REF arrow keeps ``referenz-source``; splitting the source
 * handle separates the two outgoing left-bound dashed arrows so
 * smoothstep routes them as non-overlapping parallel paths.
 *
 * Returns null only when no GS card exists either — the helper stays
 * defensive so the caller can simply skip the edge.
 */
export function pickBestellungSourceCard<
  T extends { nodeId: string; event: Record<string, unknown> },
>(
  deCards: readonly T[],
  seCards: readonly T[],
  bestellung: { entityId: string; agreementNumber: string | null },
): {
  sourceCard: T;
  sourceHandle: 'bestellung-source' | 'bestellung-from-gs';
  targetHandle: 'title-target' | 'title-target-left';
} | null {
  const matchByAgreement = (card: T) =>
    String(card.event.agreement_id ?? '') === bestellung.entityId;
  const matchByBestellNumber = (card: T) => {
    const n = card.event.bestell_number;
    return (
      typeof n === 'string' &&
      n.length > 0 &&
      bestellung.agreementNumber != null &&
      n === bestellung.agreementNumber
    );
  };
  // #888 — LS branch is BestellNr-only. The ``matchByAgreement``
  // fallback that lived here pre-#888 selected LS cards that lacked
  // the ``bestellung-source`` handle in the DOM, so the edge silently
  // disappeared. Fall through to the GS branch instead.
  const lsSourceCard = deCards.find(matchByBestellNumber) ?? null;
  const gsSourceCard = seCards.find(matchByAgreement) ?? seCards[0] ?? null;
  const sourceCard = lsSourceCard ?? gsSourceCard;
  if (!sourceCard) return null;
  // #915 — target handle depends on the source column. LS shares the
  // x column with Bestellung post-collapse; multi-tile groups may put
  // intervening tiles between source and target, so the dashed edge
  // routes Left→Left around the LEFT side of the column (smoothstep
  // with default offset, codex round 4 P2). GS lives at COL_RIGHT, so
  // its arrow lands on Bestellung's right edge (the pre-#915 default).
  //
  // #1048 — GS branch sources from ``bestellung-from-gs`` (left edge,
  // top: 22) rather than ``referenz-source`` (left edge, top: 44). The
  // GS→LS REF arrow keeps the top: 44 anchor; splitting the source
  // handle separates the two outgoing left-bound arrows so smoothstep
  // routes them as non-overlapping parallel paths instead of two
  // L-bends collapsing into the same elbow. The visual origin shifts
  // up one row — the arrow leaves at the GS title row middle, same as
  // ``title-source`` on the right edge — which keeps it
  // unambiguously the Bestellung arrow even before the operator
  // follows it to the target. AC#2 (arrowhead lands on the inside
  // edge of the target) is unchanged: targetHandle stays
  // ``title-target`` (Bestellung's right edge).
  return {
    sourceCard,
    sourceHandle: lsSourceCard ? 'bestellung-source' : 'bestellung-from-gs',
    targetHandle: lsSourceCard ? 'title-target-left' : 'title-target',
  };
}

/**
 * #1812 (FE) — decide whether a clicked canvas node opens the
 * ``DocumentPreviewPanel``, and with which document id.
 *
 * A node is a clickable "document" tile iff:
 *   • it is a line-row tile (LS / GS) — identified by the ``side`` key
 *     that only ``LineRowNodeData`` carries; OR
 *   • it is a Bestellung tile (``type === 'bestellung'``) that carries
 *     the singular order-doc provenance ``sourceDocId`` (subtask 07 /
 *     PR #1818). A Bestellung without it (stub-minted / pre-back-fill)
 *     stays second-class — no preview.
 *
 * Returns ``{ documentId }`` to emit to the parent's ``onNodeClick``
 * (``documentId`` may be ``null`` for a line-row tile whose source doc
 * is genuinely unknown — preserves the pre-#1812 behaviour), or
 * ``null`` to emit nothing at all (payout / missing-doc placeholders,
 * and provenance-less Bestellung tiles). Extracted as a pure function
 * so the gate is unit-testable without mounting ReactFlow.
 */
export function resolveNodeClickTarget(node: {
  type?: string;
  data: Record<string, unknown>;
}): { documentId: string | null } | null {
  const d = node.data;
  const isLineRow = 'side' in d;
  const sourceDocId = d.sourceDocId;
  const isBestellungWithDoc = node.type === 'bestellung' && sourceDocId != null;
  if (!isLineRow && !isBestellungWithDoc) return null;
  return { documentId: typeof sourceDocId === 'string' ? sourceDocId : null };
}

/**
 * #1176 — Decide the canvas y of a single "Bestellung fehlt" / Bestellung
 * tile, given how its dashed source arrow was resolved.
 *
 * Two routing regimes (mirrors ``pickBestellungSourceCard``'s
 * ``sourceHandle`` split):
 *
 *   • **LS-sourced** (``bestellung-source``): the tile genuinely belongs
 *     ABOVE the LS in the LEFT column (the #784/#915 collapsed stack), so
 *     it keeps the sequential ``bestellungY`` running counter — the
 *     pre-#1176 behaviour, bit-for-bit.
 *
 *   • **GS-sourced** (``bestellung-from-gs``): the citing card is a
 *     Gutschrift living in the RIGHT column at ``gsRowY``. The dashed
 *     Bestellung→GS edge should read straight, so the placeholder must
 *     sit on its GS partner's row rather than piling at the group top.
 *     Pre-#1176 the sequential counter happened to align ONLY in the 1:1
 *     case (single GS, single placeholder → counter == GS top after the
 *     ``bestellungLsgsOffset`` reservation); with one LS → many GS the
 *     counter stacked all placeholders at the top-left while their GS
 *     partners spread down the right column (the reported bug).
 *
 * Collision guard: when two GS-sourced placeholders resolve to the SAME
 * GS row y (e.g. two agreements both cited by the same GS, or the
 * defensive ``seCards[0]`` fallback firing twice), nudging both onto the
 * identical y would overlap them. Each subsequent placeholder on an
 * already-used y is offset by ``collisionStep`` so they stack just below
 * the GS row instead of overlapping. ``usedYs`` MUST be the set of y's
 * THIS function already returned for prior GS-sourced placeholders in the
 * same group (the caller threads it through the loop).
 *
 * LS-overlap guard (#1176 codex P2): LS cards live in the SAME COL_LEFT
 * column as the placeholder, and the first GS row sits ``REF_ROW_PUSH``
 * (22px) above its paired LS — so a placeholder cited by that first GS,
 * naively aligned to the GS row, would land on top of the LS card. When
 * the resolved y intersects any ``lsSpans`` interval the helper abandons
 * the GS alignment and returns the sequential above-LS y instead (the
 * shipped #784/#915 placement). Only GS rows clear of every LS span keep
 * their alignment.
 *
 * Returns ``{ y, usedGsRow }`` — ``y`` is the chosen canvas y; ``usedGsRow``
 * is true only when the tile actually took its GS row (so the caller
 * reserves it in ``usedYs`` only then, never for an LS-overlap fallback).
 * The collision + overlap bookkeeping stays testable in isolation.
 */
export function computeBestellungPlaceholderY(input: {
  /** Running top-of-group counter (pre-#1176 behaviour for LS-sourced). */
  sequentialY: number;
  /** GS card's top y when GS-sourced, else null (LS-sourced / unresolved). */
  gsRowY: number | null;
  /** y's already assigned to prior GS-sourced placeholders in this group. */
  usedYs: ReadonlySet<number>;
  /** Per-collision nudge so two placeholders on the same GS row don't overlap. */
  collisionStep: number;
  /**
   * #1176 (codex P2) — vertical spans the LS card(s) occupy in COL_LEFT,
   * the SAME column the placeholder is emitted into. The first GS row
   * sits ``REF_ROW_PUSH`` (22px) ABOVE its paired LS, so a 70px tile
   * cited by that first GS and dropped on the GS row would overlap the
   * LS card. Omitted/empty ⇒ no avoidance (the single-GS / LS-sourced
   * paths never reach the GS-alignment regime).
   */
  lsSpans?: ReadonlyArray<{ top: number; bottom: number }>;
  /** Placeholder tile height, for the LS-span intersection test. */
  tileHeight?: number;
}): { y: number; usedGsRow: boolean } {
  const { sequentialY, gsRowY, usedYs, collisionStep } = input;
  const lsSpans = input.lsSpans ?? [];
  const tileHeight = input.tileHeight ?? 0;
  // LS-sourced (or GS y unresolvable): keep the left-column top stack.
  if (gsRowY == null) return { y: sequentialY, usedGsRow: false };
  // GS-sourced: align to the GS row, stepping down past any prior
  // placeholder that already claimed this exact y.
  let y = gsRowY;
  while (usedYs.has(y)) y += collisionStep;
  // #1176 (codex P2) — refuse to drop the tile on top of an LS card.
  // When the resolved GS row lands the placeholder inside an LS card's
  // vertical span (the first-GS-cited case, where the GS row sits just
  // above its paired LS in this same COL_LEFT column), abandon the GS
  // alignment and fall back to the sequential above-LS stack — the
  // shipped #784/#915 placement for that row. ``usedGsRow=false`` tells
  // the caller NOT to reserve this y in the GS-row collision set.
  const overlapsLs = lsSpans.some((s) => y < s.bottom && y + tileHeight > s.top);
  if (overlapsLs) return { y: sequentialY, usedGsRow: false };
  return { y, usedGsRow: true };
}

export function buildPayoutColumns(
  group: MatchGroup,
  options: {
    /** #917 — each entry's ``y`` is the citing GS card's top edge in
     *  canvas coords. Abrechnung tiles top-align to this y per AC §1.
     *  ``y`` is the per-card top emitted by the paired loop (post-#914
     *  ``seCards[i].y``) or ``yOffset`` for orphan_settlement's
     *  synthetic single source card. */
    sourceCards: { nodeId: string; event: Record<string, unknown>; y: number }[];
    yOffset: number;
    /** #842 — Zahlungsavis still row-centers. ``avisShift`` keeps the
     *  pre-#842 semantics for the avis column only; Abrechnung is now
     *  per-source-GS-y (option (a) from #917 — `abrechnungShift`
     *  removed from the option shape). */
    avisShift?: number;
  },
): { nodes: Node[]; edges: Edge[]; bottomY: number } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const { sourceCards, yOffset } = options;
  const avisShift = options.avisShift ?? 0;

  // ABRECHNUNG column — top-aligned to the citing GS, with N→1 dedup
  // (#917). Each Abrechnung tile renders below the topmost source GS
  // that cites it; when a single GS cites multiple Abrechnungs, those
  // stack vertically below that GS. ``runningYByCard`` is the
  // per-source-GS y accumulator — lazy-init to the card's own y on
  // first use so cards that never cite an Abrechnung never enter the
  // ``bottomY`` envelope below.
  const matchedAbrNumbers = new Set(
    group.payoutStatements
      .map((p) => p.number)
      .filter((n): n is string => n != null && n.length > 0),
  );
  // #827 — set of ``abrechnungs_number`` strings the materialiser
  // flagged as duplicates (≥2 payout_statement_event rows share the
  // number). Diffed below to surface ``conflictSignal`` on each
  // affected Abrechnung tile so the operator sees the warning chip
  // rendered by PayoutStatementNode. Pre-#827 this list was hydrated
  // but never read by the canvas. #917 dedup is on the SOURCE GS axis
  // (N GSs cite the same number → 1 tile); the conflict axis (N PSE
  // rows share a number) is orthogonal — both PSE rows still emit
  // their own tile because we iterate ``group.payoutStatements``.
  const conflictingAbrNumbers = new Set(
    group.payoutStatementConflicts.map((c) => c.abrechnungsNumber),
  );
  // #917 — return every source card citing a given Abrechnung number
  // (was: first-match-wins). Used to compute the topmost source for
  // tile placement AND to fan one edge into the tile from each citing
  // GS — mirrors the Zahlungsavis N→1 pattern below.
  const findAllCardsByAbrNumber = (number: string) => {
    const out: typeof sourceCards = [];
    for (const card of sourceCards) {
      const n = String(card.event.abrechnungs_number ?? '');
      if (n && n === number) out.push(card);
    }
    return out;
  };
  const runningYByCard = new Map<string, number>();
  // #917 — pick the citing card with the smallest y (= topmost on
  // canvas). ``<=`` keeps the accumulator on ties, so the result is
  // stable w.r.t. ``seEvents.forEach`` insertion order.
  const pickTopmost = (cards: typeof sourceCards) =>
    cards.reduce((a, b) => (a.y <= b.y ? a : b));
  group.payoutStatements.forEach((ps, idx) => {
    const nodeId = `payout_statement_${group.id}_${idx}`;
    const citing = ps.number ? findAllCardsByAbrNumber(ps.number) : [];
    const anchors = citing.length > 0 ? citing : sourceCards[0] ? [sourceCards[0]] : [];
    if (anchors.length === 0) return;
    const topmost = pickTopmost(anchors);
    const tileY = runningYByCard.get(topmost.nodeId) ?? topmost.y;
    nodes.push({
      id: nodeId,
      type: 'payoutStatement',
      position: { x: COL_ABRECHNUNG, y: tileY },
      data: {
        entityId: ps.entityId,
        number: ps.number,
        totalAmount: ps.totalAmount,
        periodStart: ps.periodStart,
        periodEnd: ps.periodEnd,
        issuerName: ps.issuerName,
        recipientName: ps.recipientName,
        // #827 — true when this tile's number is one the materialiser
        // surfaced as a duplicate-claim collision. PayoutStatementNode
        // renders a ``⚠ Doppelte Nummer`` chip when truthy.
        conflictSignal: ps.number != null && conflictingAbrNumbers.has(ps.number),
      } satisfies PayoutStatementNodeData,
    });
    anchors.forEach((sourceCard, srcIdx) => {
      // #902 — anchor on the GS tile's right-edge title handle so
      // the arrow travels horizontally to the Abrechnung column on
      // the right. Before #902 this used ``referenz-source`` (left
      // edge, REF row), forcing smoothstep to U-turn around the
      // GS tile. #1050 — route through ``buildDashedReferenceEdge``
      // to match the dashed style of the LS←GS REF arrows. The
      // Abrechnung tile sits at ``tileY`` = the **topmost** anchor's
      // canvas y, so ``coLinear`` (→ straight router) is correct only
      // for that anchor. Non-topmost anchors in an N→1 fan would draw
      // a long diagonal across rows; fall back to the helper's
      // ``smoothstep`` branch (still dashed, rounded elbow) for those.
      // The gate is ``tileY === sourceCard.y`` (handle-y delta absorbed
      // by the helper) rather than node identity: in a per-source stack
      // ``runningYByCard`` pushes later tiles 82 px below the source
      // GS, so even the topmost source ceases to be co-linear after
      // the first tile in that stack.
      edges.push(
        buildDashedReferenceEdge({
          id: `payout_statement_${group.id}_${idx}_${srcIdx}_edge`,
          source: sourceCard.nodeId,
          sourceHandle: 'title-source',
          target: nodeId,
          targetHandle: 'title-target',
          coLinear: tileY === sourceCard.y,
        }),
      );
    });
    runningYByCard.set(topmost.nodeId, tileY + BESTELLUNG_TILE_HEIGHT + STACK_GAP);
  });
  // #917 — `new Set` wrap: the materialiser already deduplicates
  // ``expected_abrechnungs_numbers`` (worker/.../gutschrift_match_group.py:263
  // emits a set), but defensive dedup here closes the door on any
  // future caller that passes raw event-level numbers. Without this,
  // two GSs in the same group citing the same missing Abrechnung
  // would emit two ``Abrechnung fehlt`` placeholders.
  const unmatchedAbrNumbers = Array.from(
    new Set(group.expectedAbrechnungsNumbers.filter((n) => !matchedAbrNumbers.has(n))),
  );
  unmatchedAbrNumbers.forEach((number, numberIdx) => {
    const nodeId = `miss_abr_${group.id}__${number}`;
    const citing = findAllCardsByAbrNumber(number);
    const anchors = citing.length > 0 ? citing : sourceCards[0] ? [sourceCards[0]] : [];
    if (anchors.length === 0) return;
    const topmost = pickTopmost(anchors);
    const tileY = runningYByCard.get(topmost.nodeId) ?? topmost.y;
    nodes.push({
      id: nodeId,
      type: 'missingDoc',
      position: { x: COL_ABRECHNUNG, y: tileY },
      data: {
        missingSide: 'abrechnung',
        width: TILE_WIDTH,
        height: BESTELLUNG_TILE_HEIGHT,
        expectedNumber: number,
      } satisfies MissingDocNodeData,
      draggable: false,
      selectable: false,
    });
    anchors.forEach((sourceCard, srcIdx) => {
      // #902 — same right-edge title anchor on the GS source so the
      // arrow doesn't U-turn around the GS tile when targeting the
      // placeholder column on the right. #1050 — placeholder edge
      // matches the real-tile edge: routed through
      // ``buildDashedReferenceEdge`` for the same dashed look as the
      // LS←GS REF arrows, gating ``coLinear`` (→ straight) on the
      // **topmost** anchor so N→1 fans from lower rows still bend
      // through the helper's dashed-smoothstep branch instead of
      // slashing diagonally across rows. ``tileY === sourceCard.y`` is
      // the right gate (not node identity) because a single GS citing
      // N expected numbers stacks placeholders 82 px apart under it.
      edges.push(
        buildDashedReferenceEdge({
          id: `miss_abr_${group.id}_${numberIdx}_${srcIdx}_edge`,
          source: sourceCard.nodeId,
          sourceHandle: 'title-source',
          target: nodeId,
          targetHandle: 'title-target',
          coLinear: tileY === sourceCard.y,
        }),
      );
    });
    runningYByCard.set(topmost.nodeId, tileY + BESTELLUNG_TILE_HEIGHT + STACK_GAP);
  });

  // ZAHLUNGSAVIS column — real-only (no placeholder per §E.5).
  let avisY = yOffset + avisShift;
  group.payoutAdvices.forEach((pa, idx) => {
    const nodeId = `payout_advice_${group.id}_${idx}`;
    nodes.push({
      id: nodeId,
      type: 'payoutAdvice',
      position: { x: COL_ZAHLUNGSAVIS, y: avisY },
      data: {
        entityId: pa.entityId,
        number: pa.number,
        totalPaymentAmount: pa.totalPaymentAmount,
        paymentDueDate: pa.paymentDueDate,
        buyerName: pa.buyerName,
        sellerName: pa.sellerName,
      } satisfies PayoutAdviceNodeData,
    });
    const refCns = new Set(pa.referencedCreditNoteNumbers);
    const matchingCards = sourceCards.filter((card) => {
      const cn = String(card.event.credit_note_number ?? '');
      return cn.length > 0 && refCns.has(cn);
    });
    const cardsToWire = matchingCards.length > 0 ? matchingCards : sourceCards.slice(0, 1);
    cardsToWire.forEach((sourceCard, edgeIdx) => {
      edges.push({
        id: `payout_advice_${group.id}_${idx}_${edgeIdx}_edge`,
        source: sourceCard.nodeId,
        sourceHandle: 'referenz-source',
        target: nodeId,
        targetHandle: 'title-target',
        type: 'smoothstep',
        // #1122 — share ``DASHED_EDGE_OFFSET`` with
        // ``buildDashedReferenceEdge`` so both emission sites bend at
        // the same x. Legacy emission (solid stroke, no dash) →
        // can't route through the shared helper, but the
        // bend-geometry must stay in lockstep so a GS source that
        // emits both a payout_advice edge AND a sibling dashed edge
        // doesn't show inconsistent corner radii.
        ...{ pathOptions: { borderRadius: 12, offset: DASHED_EDGE_OFFSET } },
        style: { stroke: '#9ca3af', strokeWidth: 1 },
        markerEnd: { type: MarkerType.Arrow, color: '#9ca3af', width: 14, height: 14 },
      });
    });
    avisY += BESTELLUNG_TILE_HEIGHT + STACK_GAP;
  });

  // #917 — ``bottomY`` reads from ``runningYByCard`` (per-source-GS
  // stacks) instead of the legacy linear ``abrechnungY`` accumulator.
  // Each value is one ``STACK_GAP`` past the last tile that source
  // emitted, mirroring the old accumulator's tail. ``yOffset`` is the
  // zero-floor — when neither column emits a tile we still want
  // ``bottomY === yOffset`` so ``orphanRowH = max(rowH, bottomY −
  // yOffset)`` stays a no-op (``orphanRowPayoutHeight.test.ts`` test 1).
  const bottomY = Math.max(avisY, ...runningYByCard.values(), yOffset);
  return { nodes, edges, bottomY };
}

/**
 * #842 — vertically center each row's tiles against the row's midpoint.
 *
 * Pre-fix: every column (LS+GS pair, Bestellung, Abrechnung, Zahlungsavis)
 * accumulated its own ``yOffset`` from the row top. Tall GS tiles
 * (multi-line goods + footer chips) made their column stretch far below
 * the row top while shorter siblings (Bestellung / Abrechnung /
 * Zahlungsavis at the fixed ~70px tile height) hugged the top edge —
 * reading at a glance as a hanging GS rather than a row of paired
 * documents (canary: Wiesenhof April 2026 ``Ohne Zuordnung`` section).
 *
 * Post-fix: ``rowH = max(perColumnHeight)``. Each column receives a
 * y-shift = ``(rowH - colH) / 2`` so its bounding-box midpoint sits on
 * the row midpoint. The LS+GS pair is one combined column (key
 * ``"lsgs"``) so the existing ``REF_ROW_PUSH`` offset between LS top
 * and GS top — the alignment that makes the GS Ref → LS title arrow
 * run horizontally — is preserved as the pair shifts together.
 *
 * #917 narrowed the policy: the Abrechnung column is no longer
 * row-centered — each Abrechnung tile top-aligns to its citing GS's
 * top y instead (handled in ``buildPayoutColumns``). The
 * ``abrechnung`` input/output field was removed from this helper to
 * eliminate dead state. The post-emission ``Math.max(layout.rowH,
 * payoutEmission.bottomY - yOffset)`` fold in the paired-loop and
 * orphan_settlement call sites covers the Abrechnung depth without
 * needing a centering shift here.
 *
 * Exported pure so a Vitest unit test can pin the rowH + shift contract
 * without mounting the canvas (same approach as
 * ``computeMatchGraphRemountKey`` and ``buildOrphanDeliveryPosPlaceholders``).
 *
 * Shifts are rounded to integer pixels — ReactFlow translates nodes via
 * CSS transforms and half-pixel y values cause subpixel border blur on
 * default-DPR displays.
 */
export function computeRowLayout(columnHeights: {
  lsgs?: number;
  avis?: number;
}): {
  shifts: { lsgs: number; avis: number };
  rowH: number;
} {
  // #915 — Bestellung collapsed into the ``lsgs`` envelope (stacks
  // above LS at the same x), so it is no longer a separate column for
  // row-centering. The caller adds the Bestellung stack height into
  // ``lsgs`` before passing it in.
  const heights = [columnHeights.lsgs ?? 0, columnHeights.avis ?? 0];
  const rowH = Math.max(0, ...heights);
  const shiftOf = (h: number | undefined): number => {
    if (h == null || h <= 0) return 0;
    if (h >= rowH) return 0;
    return Math.round((rowH - h) / 2);
  };
  return {
    rowH,
    shifts: {
      lsgs: shiftOf(columnHeights.lsgs),
      avis: shiftOf(columnHeights.avis),
    },
  };
}

/**
 * #914 — split the paired-loop's combined ``lsgs`` envelope into
 * per-side y starts, centering the GS card inside the LS stack when
 * the LS dominates. Before #914 the loop hardcoded GS to the row top
 * and LS one ``REF_ROW_PUSH`` below, so a ``2 LS + 1 GS`` row left a
 * ~158 px GS-to-next-GS gap (vs. the expected 28 px) — the empty band
 * beneath the short GS column inflated the row's effective height
 * without redistributing the GS within it.
 *
 * When ``REF_ROW_PUSH + deColH <= seColH`` (GS dominates or single-LS
 * case), the result is bit-for-bit identical to the pre-#914 output:
 * ``seY = lsgsTop`` and ``deY = lsgsTop + refRowPush``. That is the
 * hard acceptance gate from #914 AC#2.
 *
 * When the LS stack dominates, the GS centers in the
 * ``REF_ROW_PUSH + deColH`` envelope. The REF arrow's ``coLinear``
 * flag at the paired-loop call site must be gated on ``lsDominates``
 * so the dashed arrow primitive (#843) switches from ``straight`` to
 * ``smoothstep`` once the endpoints are no longer at the same canvas y.
 *
 * Half-pixel ``seY`` is rounded to integer per the rule documented on
 * ``computeRowLayout`` above — React Flow translates nodes via CSS
 * transforms and subpixel y values blur tile borders on default DPR.
 */
export function computePairedLsgsYs(input: {
  deColH: number;
  seColH: number;
  lsgsTop: number;
  refRowPush: number;
}): { deY: number; seY: number; lsDominates: boolean } {
  const { deColH, seColH, lsgsTop, refRowPush } = input;
  const lsDominates = refRowPush + deColH > seColH;
  const deY = lsgsTop + refRowPush;
  const seY = lsDominates
    ? lsgsTop + Math.round((deColH + refRowPush - seColH) / 2)
    : lsgsTop;
  return { deY, seY, lsDominates };
}

/**
 * #1123 — single source of truth for stacked-card y-positions.
 *
 * Replaces the per-loop ``yAccumulator += nodeHeight(...) +
 * STACK_GAP`` pattern that was the recurring source of the
 * Gutschrift-Check canvas spacing drift (see auto-memory
 * ``reference_gutschrift_check_canvas_estimated_layout``). Reads
 * each card's actual rendered height from ``measuredHeights``
 * (populated by ``useMeasuredHeights``, which subscribes to xyflow
 * v12's ``nodeLookup``) and falls back to ``baselineHeights`` only
 * for cards xyflow hasn't measured yet — i.e. the first paint
 * before the per-node ResizeObserver fires.
 *
 * Invariant: ``ys[i+1] - ys[i] === heightOf(ids[i]) + stackGap``.
 * Tested in ``__tests__/stackYs.test.ts``.
 *
 * @param input.cardIds  Ids of cards in this stack, in render order.
 * @param input.baselineHeights  Pre-measure heights, indexed
 *   parallel to ``cardIds``. Used only until xyflow measures each
 *   card; ``nodeHeight(linesCount)`` is the canonical baseline.
 * @param input.measuredHeights  Live measured heights, keyed by
 *   node id. Reference identity is stable across non-measurement
 *   store ticks (see ``useMeasuredHeights``).
 * @param input.startY  Canvas y of the first card.
 * @param input.stackGap  Vertical gap between consecutive cards.
 */
export function stackYs(input: {
  cardIds: readonly string[];
  baselineHeights: readonly number[];
  measuredHeights: ReadonlyMap<string, number>;
  startY: number;
  stackGap: number;
}): number[] {
  const { cardIds, baselineHeights, measuredHeights, startY, stackGap } = input;
  const ys: number[] = [];
  let y = startY;
  for (let i = 0; i < cardIds.length; i++) {
    ys.push(y);
    const measured = measuredHeights.get(cardIds[i]);
    const h = typeof measured === 'number' ? measured : (baselineHeights[i] ?? 0);
    y += h + stackGap;
  }
  return ys;
}

/**
 * #1123 — bounding-box height of a vertical stack. Mirrors
 * ``stackYs``'s height-resolution policy (measured > baseline)
 * but returns a single scalar: the sum of per-card heights plus
 * ``(N-1) * stackGap``. There is NO trailing stack-gap after the
 * last card — the result is the column's content envelope, fed
 * into ``computeRowLayout`` to decide where to center each column.
 *
 * Returns 0 for an empty stack so callers (e.g. ``bestellungH``
 * or ``avisH`` when no Bestellung / Zahlungsavis tiles exist)
 * don't need a length guard.
 */
export function stackHeight(input: {
  cardIds: readonly string[];
  baselineHeights: readonly number[];
  measuredHeights: ReadonlyMap<string, number>;
  stackGap: number;
}): number {
  const { cardIds, baselineHeights, measuredHeights, stackGap } = input;
  if (cardIds.length === 0) return 0;
  let total = 0;
  for (let i = 0; i < cardIds.length; i++) {
    const measured = measuredHeights.get(cardIds[i]);
    total += typeof measured === 'number' ? measured : (baselineHeights[i] ?? 0);
  }
  return total + stackGap * (cardIds.length - 1);
}

// #784 / #915: column geometry. Originally a 2-column layout (LS|GS),
// then #784 added BESTELLUNG as a 3rd column at negative x to the left
// of LS. #915 collapsed BESTELLUNG back into the LS column — Bestellung
// tile(s) now stack vertically ABOVE the LS tile at the same x =
// COL_LEFT. The visual order (Bestellung → LS → GS → Abrechnung →
// Zahlungsavis, top-to-bottom in the left column, then left-to-right
// across columns) matches the upstream → downstream harvest-chain flow.
const COL_LEFT = 20;       // delivery (LS) + bestellung x — left column
const COL_RIGHT = 440;     // settlement (GS) nodes x — middle column
const COL_MID = 240;       // stub marker x (mid-gap between LS and GS)
const TILE_WIDTH = 380;    // matches .line-row-node max-width
// #793 phase 4 — Abrechnung (column 4) + Zahlungsavis (column 5).
// Gap to GS card right edge (COL_RIGHT + TILE_WIDTH = 820) is 40 —
// same column-to-column spacing as the LS/GS gap (440 - 400 = 40), so
// the canvas reads as a uniform 5-column grid.
const COL_ABRECHNUNG = 860;     // payout_statement_event tiles x
const COL_ZAHLUNGSAVIS = 1280;  // payout_advice_event tiles x
const BESTELLUNG_TILE_HEIGHT = 70; // header + orgs row, no line subtree
// Within-group vertical gap between stacked cards on the same side
// (1:N rendering). Smaller than ``GROUP_GAP`` so the cards read as
// "members of the same component", not "two unrelated groups".
// File-local because both the per-group layout loop and the
// extracted ``buildPayoutColumns`` helper (#793 phase 4) use it.
const STACK_GAP = 12;
// #1160 — vertical gap between stacked fixed-height "Gutschrift POS …
// (fehlt)" placeholders in the orphan_delivery band
// (``buildOrphanDeliveryPosPlaceholders``). Distinct from ``STACK_GAP``
// (the real-card stack gap) so the typed-token stub stack keeps its
// pre-#1160 8-px rhythm.
const POS_TOKEN_GAP = 8;
// #1123 — sentinel ``measuredHeights`` for fixed-height column
// envelopes (Bestellung, Zahlungsavis). Those tiles are positioned
// by the BESTELLUNG_TILE_HEIGHT constant — the envelope MUST be
// computed from the same baseline arithmetic or envelope-math and
// placement-math can drift on font-load shifts. Module-level so the
// reference identity is stable across renders (won't bust the
// ``layout`` memo on every render). Codex round 1 P2.
const EMPTY_MEASURED_HEIGHTS: ReadonlyMap<string, number> = new Map();
const GROUP_GAP = 28;      // vertical gap between groups (more breathing room)
const ORPHAN_BAND_GAP = 56; // larger gap before orphan band starts
const LINE_HEIGHT = 30;   // px per line item inside a node
const NODE_HEADER = 60;   // approx header+orgs height before first line (px)
const NODE_PADDING = 10;  // top padding
// Height of the GS-only "Ref: ..." sub-header row. Paired LS tiles are
// shifted down by this amount so the dividers (and the horizontal lines
// of produce rows that follow) sit at the same canvas y on both sides.
// Without the shift, the GS divider lands ~22px below the LS divider and
// the eye reads the two cards as off by one row. The REF arrow source
// handle on the GS tile is co-located so the arrow runs perfectly
// horizontal from GS Ref row → LS title (see LineRow.tsx).
const REF_ROW_PUSH = 22;
// Extra vertical room reserved when a tile renders the #781 POS row.
// Matches the fixed ``height: 22px`` on ``.line-row-node__referenz``
// (see LineRow.css). NODE_HEADER already accounts for the Ref row but
// not for the POS row, so we add this only when a card actually
// carries a POS token — otherwise stacked cards / orphan stubs would
// reserve unused vertical space everywhere.
const POS_ROW_HEIGHT = 22;

/** Whether a given event entity will render the #781 POS chip on its
 * tile. The attribute name diverges between subclasses:
 *
 * * ``delivery_event`` writes the POS token onto ``pos_reference``
 *   (see ``link_to_entities.py:2287-2308``).
 * * ``settlement_event`` writes it onto ``reference`` (the
 *   harvest-credit-note ``document_sources`` field_mapping declares
 *   ``pos_number: reference`` — confirmed by the e2e
 *   ``twin-pos-resale-fk`` test which matches
 *   ``settlement.reference`` against ``delivery.pos_reference``).
 *   We also accept ``pos_reference`` on settlement as a forward-
 *   compat alias for any future ontology that lands on it directly.
 *
 * Used by both ``buildLineRowNode`` (the prop derivation) and the
 * layout pass (``nodeHeight``) so the rendered height stays in sync
 * with the reserved height — codex P2 on PR #795 caught the
 * desynchronisation that let stacked cards overlap.
 */
export function eventPosToken(
  side: 'delivery' | 'settlement',
  event: Record<string, unknown>,
): string | undefined {
  const candidate =
    side === 'settlement'
      ? (event.reference ?? event.pos_reference)
      : event.pos_reference;
  return typeof candidate === 'string' && candidate.length > 0 ? candidate : undefined;
}

/**
 * Pre-measurement baseline height of an LS/GS tile.
 *
 * #1123: ``nodeHeight`` is no longer the source of truth for between-
 * tile y-stacking — xyflow v12's per-node ResizeObserver populates
 * ``node.measured.height`` and the layout pass reads it via
 * ``useMeasuredHeights`` + ``stackYs`` / ``stackHeight``. This
 * function is the *first-frame baseline*, used only until the
 * ResizeObserver fires for each tile (typically within the first
 * paint cycle). It deliberately drops the per-subtitle-row flags
 * (``hasPosRow`` / ``hasBestellRow`` / ``hasLieferterminRow``) that
 * pre-#1123 layout depended on: those flags were the recurring
 * source of the canvas spacing drift (auto-memory
 * ``reference_gutschrift_check_canvas_estimated_layout``) — every
 * new LineRow subtitle required a matching flag here, and humans
 * have demonstrably failed to keep that contract.
 *
 * The baseline is intentionally rough. The first frame may show a
 * few pixels of drift; the very next frame (after measurement)
 * snaps every card to its true height. Within-tile coordinates
 * (the dashed quantity_mismatch stub markers, anchored via
 * ``lineYCenter``) still use the subtitle-flag formula because
 * those are within-tile coordinates whose row layout we *do* know
 * by construction.
 */
function nodeHeight(linesCount: number) {
  return NODE_PADDING + NODE_HEADER + linesCount * LINE_HEIGHT + NODE_PADDING;
}

/** Y-center of line at index i within a node that starts at yOffset.
 *  When the card carries a POS chip we reserve ``POS_ROW_HEIGHT`` *before*
 *  the line rows in ``nodeHeight`` (#781), so stub-marker placement and
 *  any other line-center math must add the same offset or the dashed
 *  ``quantity_mismatch`` stub edge points one POS-row above the actual
 *  handle. Codex round 2 on PR #795 (P2). #833 extends the same offset
 *  reservation to the LS-side BestellNr row so stub markers on an LS
 *  card that carries the new subtitle still line up with their handles.
 */
export function lineYCenter(
  yOffset: number,
  lineIndex: number,
  hasPosRow: boolean = false,
  hasBestellRow: boolean = false,
  hasLieferterminRow: boolean = false,
): number {
  return (
    yOffset +
    NODE_PADDING +
    NODE_HEADER +
    (hasPosRow ? POS_ROW_HEIGHT : 0) +
    (hasBestellRow ? POS_ROW_HEIGHT : 0) +
    // #889 — Liefertermin subtitle (#839) sits in the same subtitle
    // stack as POS / BestellNr. Stub-marker placement reads this so
    // the dashed quantity_mismatch stub still points at the actual
    // line handle on LS cards that carry the Liefertermin row.
    (hasLieferterminRow ? POS_ROW_HEIGHT : 0) +
    lineIndex * LINE_HEIGHT +
    LINE_HEIGHT / 2
  );
}

// ``formatDate`` + ``headerDateValue`` relocated to
// ``deliveryGroupSummary.ts`` (#1289 C2) — imported above,
// ``headerDateValue`` re-exported for the #839 unit test.

/**
 * #839 — Liefertermin chip value for the LS tile.
 *
 * Returns the formatted ``delivery_date`` (the post-split source-side
 * field that now carries only the Liefertermin) when it's distinct
 * from the header date. Returns undefined on the settlement side or
 * when the LS prints no Liefertermin line. Returns undefined on
 * legacy rows where ``document_date`` was never extracted — there
 * the header still carries the (conflated) Liefertermin via fallback,
 * and we don't want to render the same date twice.
 */
export function lieferterminChipValue(
  side: 'delivery' | 'settlement',
  event: Record<string, unknown>,
): string | undefined {
  if (side !== 'delivery') return undefined;
  const documentDate = event.document_date;
  // Legacy row: no distinct document_date was extracted. The header
  // already shows the Liefertermin (via fallback) — don't duplicate.
  if (typeof documentDate !== 'string' || documentDate.length === 0) {
    return undefined;
  }
  const liefertermin = event.delivery_date;
  if (typeof liefertermin !== 'string' || liefertermin.length === 0) {
    return undefined;
  }
  // Suppress same-date repeats. LineRow re-checks the formatted
  // equality, but this earlier raw-ISO check also avoids handing
  // the renderer a value it will only throw away.
  if (liefertermin === documentDate) return undefined;
  return formatDate(liefertermin);
}

/**
 * Sort lines by item count (Kolli) descending so the heaviest produce row
 * sits at the top across every card. Cross-card alignment matters because
 * a reviewer reads "LS biggest line — GS biggest line" as a paired glance;
 * if one card is ordered by Pal and the other by Kolli, the rows desync
 * and the eye has to hunt. Status (matched / quantity_mismatch) is the
 * tiebreaker — once a real line-matcher runs, matched rows still rise
 * within a same-count cluster.
 */
function itemCount(line: MatchGroupLine): number {
  // Prefer a real Kolli figure over a Pal figure: Kolli is the apples-to-
  // apples produce count across LS and GS. Pal is just stack geometry and
  // can mislead the eye when one side reports "5 Pal" against "146 Kolli".
  if (line.quantity != null && line.quantityUnit && line.quantityUnit.toLowerCase() !== 'pal') {
    return line.quantity;
  }
  if (line.resolvedKolliCount != null) return line.resolvedKolliCount;
  if (line.quantity != null) return line.quantity;
  return 0;
}

function sortLines(lines: MatchGroupLine[]): MatchGroupLine[] {
  // HOF-215 — ``price_missing`` sorts beside ``quantity_mismatch``: both are
  // deviations the operator is looking for, and a line credited at nothing
  // belongs next to a line credited for the wrong quantity, not below an
  // unpaired one. Typed as the full record so a future member cannot be added
  // to ``MatchLineStatus`` without landing here.
  const statusOrder: Record<MatchLineStatus, number> = {
    matched: 0,
    quantity_mismatch: 1,
    price_missing: 1,
    unmatched: 2,
  };
  return [...lines].sort((a, b) => {
    const dc = itemCount(b) - itemCount(a);
    if (dc !== 0) return dc;
    return (statusOrder[a.matchStatus] ?? 2) - (statusOrder[b.matchStatus] ?? 2);
  });
}

// ``eurFmt`` consolidated into ``deliveryGroupSummary.ts`` (#1289 C2).

function readNumber(ev: Record<string, unknown>, ...names: string[]): number | null {
  for (const n of names) {
    const raw = ev[n];
    if (raw == null) continue;
    const num = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isFinite(num)) return num;
  }
  return null;
}

/**
 * Money hint for the missing-side stub of an orphan group.
 *
 *   - orphan_delivery (LS present, GS missing): use the shared
 *     ``estimateOrphanDeliveryValue`` to project a per-tile €
 *     estimate from the cross-month price index (same anchors the
 *     dashboard tile uses). Rendered with the "ca." prefix to flag
 *     that this is derived from nearest-product paired evidence, not
 *     a real Gutschrift on file. Falls back to "Gutschrift erwartet"
 *     when no price anchor was found in the index — symmetric with
 *     the dashboard's "kein Preisanker" fallback.
 *   - orphan_settlement (GS present, LS missing): pull the GS's
 *     ``total_amount`` directly so the operator sees "this is the
 *     amount that was credited without a supporting delivery". Same
 *     figure as the dashboard's per-tile contribution to "Lieferschein
 *     fehlt", so the two views stay coherent.
 */
function missingMoneyHint(
  group: MatchGroup,
  estimatedDeliveryValue: number | null,
): string | undefined {
  if (group.status === 'orphan_delivery') {
    if (estimatedDeliveryValue != null && estimatedDeliveryValue > 0) {
      return `Gutschriftbetrag ca. ${eurFmt.format(estimatedDeliveryValue)}`;
    }
    return 'Gutschrift erwartet';
  }
  if (group.status === 'orphan_settlement') {
    const total = group.settlementEvent
      ? readNumber(group.settlementEvent as Record<string, unknown>, 'total_amount')
      : null;
    if (total != null && total > 0) {
      return `Gutschriftbetrag ${eurFmt.format(total)}`;
    }
    return 'Lieferschein erwartet';
  }
  return undefined;
}

/**
 * Mirror of LineRow's display-line filter — count the lines that will
 * actually render as goods rows in a tile, so the stub on the
 * opposite side can size itself to match. LineRow folds non-goods
 * rows (USt, Kommission, packaging) into a footer chip strip, which
 * occupies far less vertical space than ``LINE_HEIGHT`` per row.
 */
function visibleLineCount(lines: MatchGroupLine[]): number {
  const everyUntagged = lines.every((l) => !l.resourceCategory);
  if (everyUntagged) return lines.length;
  return lines.filter((l) => PRODUCE_CATEGORIES.has(l.resourceCategory ?? '')).length;
}

function hasFooterChips(lines: MatchGroupLine[]): boolean {
  const everyUntagged = lines.every((l) => !l.resourceCategory);
  if (everyUntagged) return false;
  return lines.some((l) => l.resourceCategory && !PRODUCE_CATEGORIES.has(l.resourceCategory));
}
// Approximate vertical space a footer chip strip adds when present
// (the wrap usually lands at 1-2 rows; conservative single-row
// allowance keeps the stub from outgrowing the tile when there are
// only a few chips).
const FOOTER_STRIP_HEIGHT = 36;

/**
 * #1123 — pre-measurement baseline height of an LS/GS tile (visible
 * lines + footer strip allowance). Mirrors ``nodeHeight`` in role:
 * used as the first-frame fallback before xyflow measures each
 * rendered tile. The real per-card height — including any subtitle
 * rows, wrapping footer chips, or future conditional renders — is
 * read from the measured DOM via ``useMeasuredHeights``.
 *
 * Inlined helper (not exported) since there are no external
 * consumers: the public ``renderedTileHeight`` was removed in #1123
 * along with its brittle per-subtitle-row flag parameters.
 */
function baselineTileHeight(lines: MatchGroupLine[]): number {
  const visible = visibleLineCount(lines) || 1;
  const footer = hasFooterChips(lines) ? FOOTER_STRIP_HEIGHT : 0;
  return nodeHeight(visible) + footer;
}

interface MatchGraphProps {
  groups: MatchGroup[];
  /** Unfiltered set of all groups for the month — used to build the
   *  price index so per-tile orphan-delivery estimates stay stable
   *  even when the operator filters the canvas down to just orphans
   *  (otherwise filtering "Kein Gutschrift" would strip out the very
   *  paired evidence the index draws on). Falls back to ``groups``
   *  when omitted. */
  allGroups?: MatchGroup[];
  selectedDocId?: string | null;
  onNodeClick?: (info: { documentId: string | null }) => void;
  onPaneClick?: () => void;
  // Per-entity optimistic override applied on top of each group's
  // persisted ``manuallyResolvedAt`` attribute. ``true`` = optimistically
  // marked resolved while the editEntity write is in flight; ``false``
  // = optimistically reverted. Keyed by the orphan-side entity_id
  // (settlement_event id for orphan_settlement, delivery_event id for
  // orphan_delivery). Empty / missing entries fall through to the
  // persisted state.
  resolvedOverrides?: ReadonlyMap<string, boolean>;
  // Called when the operator picks "Als erledigt markieren" on a
  // still-unresolved orphan tile. Receives the entity type
  // (``settlement_event`` | ``delivery_event``) + the orphan entity
  // id so the caller can route the editEntity write to the correct
  // /v1/twin/entities/{type}/{id} path.
  onMarkResolved?: (entityType: 'settlement_event' | 'delivery_event', entityId: string) => void;
  // Called when the operator picks "Erledigung rückgängig machen" on
  // an already-resolved orphan tile. Same shape as ``onMarkResolved``.
  onUnmarkResolved?: (entityType: 'settlement_event' | 'delivery_event', entityId: string) => void;
  // #1395 — per-event optimistic suppression override, keyed by the
  // event entity id. ``true`` = optimistically suppressed (the tile
  // renders muted with a "Wieder einblenden" action) while the
  // editEntity write is in flight; ``false`` = optimistically reverted.
  // Layered on top of the persisted ``suppressed_at`` attribute.
  suppressOverrides?: ReadonlyMap<string, boolean>;
  // Called when the operator picks "Beleg ausblenden" on an erroneous
  // document tile (QK / Papierbestellung). Routes the editEntity write
  // of ``suppressed_at`` to the correct entity. Same shape as
  // ``onMarkResolved``.
  onSuppress?: (entityType: 'settlement_event' | 'delivery_event', entityId: string) => void;
  // Called when the operator picks "Wieder einblenden" on an
  // already-suppressed tile (reversal). Same shape as ``onSuppress``.
  onUnsuppress?: (entityType: 'settlement_event' | 'delivery_event', entityId: string) => void;
  // #1396 — per-event optimistic flag override, keyed by the event entity
  // id. ``true`` = optimistically flagged while the editEntity write is in
  // flight; ``false`` = optimistically un-flagged. Layered on top of the
  // persisted ``flagged_at`` attribute. Independent of resolution +
  // suppression — a flag is NON-destructive (the tile stays in every view
  // either way), so there is no group-level exclusion for it.
  flagOverrides?: ReadonlyMap<string, boolean>;
  // #1396 — per-event optimistic comment override, keyed by the event
  // entity id. Carries the in-flight free-text note so the tile's flag
  // tooltip reflects the just-typed comment before the worker drains.
  // ``null`` = explicitly cleared (un-flag). Absent = trust the persisted
  // ``flag_comment``.
  flagCommentOverrides?: ReadonlyMap<string, string | null>;
  // Called when the operator picks "markieren & kommentieren" on a tile.
  // Receives the entity type + id + the free-text comment the operator
  // entered (already prompted by the caller) so the caller can route the
  // editEntity write of ``flagged_at`` + ``flag_comment`` to the right
  // /v1/twin/entities/{type}/{id} path. The kebab edit re-flag path reuses
  // this with the existing comment pre-filled.
  onFlag?: (
    entityType: 'settlement_event' | 'delivery_event',
    entityId: string,
    comment: string,
  ) => void;
  // Called when the operator picks "Markierung entfernen" on an
  // already-flagged tile (reversal — clears ``flagged_at`` + ``flag_comment``).
  onUnflag?: (entityType: 'settlement_event' | 'delivery_event', entityId: string) => void;
  // #1051 — manual line-link tool. The canvas calls these when the
  // operator drags between two line spots (``onManualLink``) or
  // selects an existing green edge and presses Backspace/Delete
  // (``onManualUnlink``). Both take normalised line-id args (delivery
  // = LS side, settlement = GS side); the canvas resolves which side
  // is which via the per-group line→side map it builds locally so
  // callers don't have to know about LineRow's left/right rendering.
  onManualLink?: (args: { delivery_line_id: string; settlement_line_id: string }) => Promise<unknown>;
  onManualUnlink?: (args: { delivery_line_id: string; settlement_line_id: string }) => Promise<unknown>;
  // HOF-219 — the DOCUMENT-level unlink. Called when the operator severs a
  // Gutschrift↔Lieferschein edge. Absent in Storybook / standalone renders,
  // which is what makes the affordance inert there (AC 14) — the same gate
  // ``onManualUnlink`` already applies one layer down.
  onDocumentUnlink?: (args: {
    settlement_event_id: string;
    delivery_event_id: string;
  }) => Promise<unknown>;
}

/**
 * #797 / #915 — Build the ReactFlow remount key.
 *
 * Group-ID set alone is sufficient post-#915: Bestellung no longer
 * lives in its own off-canvas column, so the viewport always sits at
 * x = 0 and the rolling-rematerialise zero→some-Bestellung transition
 * no longer needs a forced remount to re-apply a pan. The legacy
 * ``anyBestellung`` bit (#797) was dropped along with the leftward
 * viewport pan.
 *
 * Exported so a Vitest unit test can pin the contract without
 * mounting ReactFlow.
 */
export function computeMatchGraphRemountKey(
  groupIds: readonly string[],
): string {
  return groupIds.join('|') || 'empty';
}

/**
 * #1442 — flag display + kebab state for a single line-row tile.
 *
 * The flag (``flagged_at`` / ``flag_comment``) is a NON-destructive
 * annotation that must stay visible AND removable regardless of the
 * group's current match status. The pre-#1442 ``buildLineRowNode``
 * inline derivation force-disabled the flag marker and dropped the
 * edit/remove kebab actions once a previously orphan/partial group
 * rematerialised as ``matched`` (because both were gated on
 * ``isActionableSide``). This helper un-gates the flag *display* and the
 * edit/remove actions from actionability — they hang off a flag-scoped
 * entity id read straight off the event — while keeping the FRESH-flag
 * "markieren & kommentieren" action actionable-only (we don't introduce
 * brand-new flags on matched tiles).
 *
 * Resolve / suppress stay actionable-only and are NOT handled here — see
 * ``buildLineRowNode``.
 *
 * Override precedence mirrors ``isEventFlagged`` / the resolve/suppress
 * paths: an optimistic override (set the instant the operator clicks)
 * wins, otherwise the persisted attribute is the source of truth.
 */
export function buildLineRowFlagState(params: {
  event: Record<string, unknown>;
  flagOverrides?: ReadonlyMap<string, boolean>;
  // The comment override may carry ``null`` to optimistically CLEAR a
  // comment (mirrors the component prop); the ``typeof === 'string'``
  // guard below resolves a ``null`` override to ``undefined`` flagComment.
  flagCommentOverrides?: ReadonlyMap<string, string | null>;
  isActionableSide: boolean;
  entityType: 'settlement_event' | 'delivery_event';
  onFlag?: (
    entityType: 'settlement_event' | 'delivery_event',
    entityId: string,
    comment: string,
  ) => void;
  onUnflag?: (
    entityType: 'settlement_event' | 'delivery_event',
    entityId: string,
  ) => void;
}): { isFlagged: boolean; flagComment: string | undefined; flagActions: LineRowNodeData['actions'] } {
  const { event, flagOverrides, flagCommentOverrides, isActionableSide, entityType, onFlag, onUnflag } =
    params;

  // Flag-scoped id read straight off the event — NOT gated on
  // ``isActionableSide`` (the #1442 fix). A missing id means there is no
  // override-key / kebab target, so the tile is treated as un-flaggable.
  const flagEntityId = String(event.entity_id ?? '');

  const flagOverride = flagEntityId ? flagOverrides?.get(flagEntityId) : undefined;
  const rawFlaggedAt = event.flagged_at;
  const persistedFlagged = typeof rawFlaggedAt === 'string' && rawFlaggedAt.length > 0;
  const isFlagged = flagEntityId
    ? flagOverride !== undefined
      ? flagOverride
      : persistedFlagged
    : false;

  // Comment: optimistic override wins (carries the just-typed note), else
  // the persisted ``flag_comment``. Empty / whitespace / non-string ⇒
  // undefined so the badge falls back to a generic tooltip.
  const commentOverride = flagEntityId ? flagCommentOverrides?.get(flagEntityId) : undefined;
  const rawComment = commentOverride !== undefined ? commentOverride : event.flag_comment;
  const flagComment =
    typeof rawComment === 'string' && rawComment.trim().length > 0 ? rawComment.trim() : undefined;

  const flagActions: NonNullable<LineRowNodeData['actions']> = [];
  if (flagEntityId) {
    if (isFlagged) {
      // Edit + remove stay available even on a non-actionable (matched)
      // tile — the persisted flag must remain removable. ``onFlag`` is
      // pre-filled with the existing comment so the re-flag prompt edits
      // the note in place.
      if (onFlag) {
        flagActions.push({
          label: 'Markierung bearbeiten',
          onClick: () => onFlag(entityType, flagEntityId, flagComment ?? ''),
        });
      }
      if (onUnflag) {
        flagActions.push({
          label: 'Markierung entfernen',
          onClick: () => onUnflag(entityType, flagEntityId),
        });
      }
    } else if (isActionableSide && onFlag) {
      // Fresh-flag stays actionable-only: we don't offer NEW flags on
      // matched tiles (acceptance is "existing flag visible + removable").
      flagActions.push({
        label: 'markieren & kommentieren',
        onClick: () => onFlag(entityType, flagEntityId, ''),
      });
    }
  }

  return { isFlagged, flagComment, flagActions: flagActions.length > 0 ? flagActions : undefined };
}

export default function MatchGraph(props: MatchGraphProps) {
  // #785 — wrap in ReactFlowProvider so the sibling VerticalScrollbar
  // can use ``useViewport`` / ``useReactFlow`` to mirror + drive
  // viewport.y. Without an outer provider those hooks would only work
  // inside ``<ReactFlow>``'s implicit context.
  return (
    <ReactFlowProvider>
      <MatchGraphInner {...props} />
    </ReactFlowProvider>
  );
}

function MatchGraphInner({
  groups,
  allGroups,
  selectedDocId,
  onNodeClick,
  onPaneClick,
  resolvedOverrides,
  onMarkResolved,
  onUnmarkResolved,
  suppressOverrides,
  onSuppress,
  onUnsuppress,
  flagOverrides,
  flagCommentOverrides,
  onFlag,
  onUnflag,
  onManualLink,
  onManualUnlink,
  onDocumentUnlink,
}: MatchGraphProps) {
  // #1051 — lineId → (side, groupId) lookup. Used by isValidConnection
  // to reject cross-group connections and by onConnect/onEdgesDelete
  // to normalise the line-pair args (the user can drag either
  // direction; the backend always takes delivery first).
  const lineSideMap: LineSideMap = useMemo(() => buildLineSideMap(groups), [groups]);

  // #1087 — toast handle used by ``handleConnect`` to surface backend
  // ``detail.error_kind`` rejections instead of leaking them as
  // unhandled promise rejections. App.tsx wraps the tree in
  // ``ToastProvider`` so ``useToast`` is always available here.
  const { showToast } = useToast();

  // #1051 — optimistic edges. Populated in ``onConnect`` between the
  // drag drop and the mutation settling so the canvas paints the
  // green edge immediately; cleared after the mutation resolves
  // (either the derived-fact refetch lands a "real" edge with the
  // same id, or the mutation errors and the optimistic edge needs
  // to disappear).
  const [optimisticEdges, setOptimisticEdges] = useState<Edge[]>([]);

  // HOF-216 — which edges the operator has selected. Held here rather than in
  // xyflow's store because the edges array is derived from the match groups on
  // every render; see ``handleEdgesChange`` below for why the store cannot
  // keep it. Reset for free when the canvas remounts on a month switch
  // (``computeMatchGraphRemountKey``), so a stale id cannot outlive its edge.
  const [selectedEdgeIds, setSelectedEdgeIds] = useState<ReadonlySet<string>>(new Set());

  // #1123 — measured tile heights, populated by xyflow v12's
  // per-node ResizeObserver. The layout pass below consults this
  // map at every height-read site (paired-loop precompute, per-card
  // y-advance, orphan-row rowH); ``baselineTileHeight`` /
  // ``nodeHeight`` are the first-frame fallback before the
  // ResizeObserver fires. Reference identity is stable across
  // store ticks that don't change a measurement, so the
  // ``useMemo`` below only re-runs when an actual measurement
  // changes (e.g. footer chips wrap on a re-render). See
  // ``useMeasuredHeights.ts`` + ``stackYs`` / ``stackHeight``.
  const measuredHeights = useMeasuredHeights();

  const layout = useMemo(() => {
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    // #3237 — ``nodeId → {side, groupId}`` for every rendered LS/GS card.
    // A header-to-header drag lands on two CHROME handles, which carry
    // no line id, so ``lineSideMap`` alone can only classify the drop as
    // ``non_line``. This map lets the toast tell "you picked the right
    // two documents, wrong spot" (actionable copy naming the Waren-line
    // dots) from "you dropped on nothing linkable" (the blunt copy).
    // Populated at the single card-id mint site inside
    // ``buildLineRowNode`` so the ids can never drift from the nodes.
    const cardSideMap = new Map<string, LineSideEntry>();
    // #844 — Per-row grid snapshot for the arrow-key reducer. One row
    // per group rendered (paired + orphan, in canvas order top→bottom).
    // Each row carries up to 5 ``TileRef`` cells keyed by canvas
    // column. Tiles that aren't real "documents" (the missing-doc
    // placeholders) carry ``docId: null`` so the focus ring renders
    // but the preview-panel selection stays cleared — the operator
    // shouldn't get a blank Vorschau on a placeholder. Built
    // alongside the existing nodes/edges pipeline so we don't pay a
    // second derivation pass per render.
    const gridRows: NavGridRow[] = [];
    /** Helper: create a fresh empty row. Called once per group at the
     *  start of its render block, then mutated as columns get filled. */
    const newRow = (): NavGridRow => ({
      bestellung: null,
      lieferschein: null,
      gutschrift: null,
      abrechnung: null,
      zahlungsavis: null,
    });
    /** Helper: build a TileRef for the current row + column. ``docId``
     *  is the source doc id when the tile points at a real document
     *  (``null`` for placeholders so Esc-clear semantics keep the
     *  preview blank). */
    const tileRef = (
      column: NavColumnKey,
      rowIndex: number,
      nodeId: string,
      docId: string | null,
    ): NavTileRef => ({ column, rowIndex, nodeId, docId });
    let yOffset = 20;

    // Build the price index off the unfiltered set when available so
    // the per-tile orphan-delivery estimate remains coherent across
    // status filters (a "Kein Gutschrift"-only view still uses the
    // month's paired anchors for its "Gutschriftbetrag ca. X €"
    // hints). Computed inside ``useMemo`` so it tracks alongside the
    // nodes/edges derivation rather than recomputing on every render.
    const priceIndex = buildPriceIndex(allGroups ?? groups);

    // Partition: paired groups vs orphans. Render paired first (top), then
    // a banner, then orphans packed two-per-row so an unmatched LS sits in
    // the left column while an unmatched GS sits in the right — same x
    // discipline as paired rows, but no edge between them so the absence
    // of a match is visually clear.
    const isPaired = (g: MatchGroup) => !!g.deliveryEvent && !!g.settlementEvent;
    const pairedGroups = groups.filter(isPaired);
    const orphanLS = groups.filter((g) => g.deliveryEvent && !g.settlementEvent);
    const orphanGS = groups.filter((g) => !g.deliveryEvent && g.settlementEvent);

    // Sort each band by document date desc — newest at the top. The
    // reviewer's workflow is "what arrived this week?" so the freshest
    // documents should sit at the top of the canvas; older items can be
    // scrolled to. Date strings in our pipeline are ISO (``YYYY-MM-DD``
    // or full ISO timestamps), so lexicographic compare matches
    // chronological order. Paired rows sort by the LS (delivery) date
    // because that's the goods-flow anchor — a GS arrives after its LS
    // and is reasoning about *that* LS's week.
    const readEventDate = (ev: MatchGroup['deliveryEvent']): string => {
      if (!ev) return '';
      const raw =
        (ev as Record<string, unknown>).event_date ??
        (ev as Record<string, unknown>).delivery_date ??
        (ev as Record<string, unknown>).credit_date ??
        (ev as Record<string, unknown>).settlement_date ??
        (ev as Record<string, unknown>).document_date;
      return typeof raw === 'string' ? raw : '';
    };
    const byDateDesc = (a: string, b: string) => (a > b ? -1 : a < b ? 1 : 0);
    pairedGroups.sort((a, b) =>
      byDateDesc(readEventDate(a.deliveryEvent), readEventDate(b.deliveryEvent)),
    );
    orphanLS.sort((a, b) =>
      byDateDesc(readEventDate(a.deliveryEvent), readEventDate(b.deliveryEvent)),
    );
    orphanGS.sort((a, b) =>
      byDateDesc(readEventDate(a.settlementEvent), readEventDate(b.settlementEvent)),
    );

    const buildLineRowNode = (
      group: MatchGroup,
      side: 'delivery' | 'settlement',
      x: number,
      y: number,
      // Multi-event groups (one LS receiving N Gutschriften, or one GS
      // spanning N Lieferscheine) render N cards per affected side, one
      // per event in ``allSettlementEvents`` / ``allDeliveryEvents``.
      // ``event`` is the specific entity this card represents; ``lines``
      // is pre-filtered to that event's own lines. ``cardIndex`` (0-based
      // within the group/side) keeps node ids unique across cards.
      // ``referencesDelivery`` lets a settlement card show *its own* Ref
      // header in 1:N rendering (each GS may cite a different LS).
      event?: Record<string, unknown> | null,
      lines?: MatchGroupLine[],
      cardIndex: number = 0,
      referencesDelivery?: Record<string, unknown> | null,
    ): { node: Node | null; nodeId: string; docId: string | null } => {
      const resolvedEvent =
        event ?? (side === 'delivery' ? group.deliveryEvent : group.settlementEvent);
      if (!resolvedEvent) return { node: null, nodeId: '', docId: null };
      const resolvedLines = lines ?? (side === 'delivery' ? group.deliveryLines : group.settlementLines);
      const sortedLines = sortLines(resolvedLines);
      const docId = (resolvedEvent.source_doc_id as string | undefined) ?? null;
      const active = !!selectedDocId && docId === selectedDocId;
      // Selection visuals live on the inner ``.line-row-node--selected``
      // class (see LineRow.css) so the indigo tint extends across the
      // tile border itself. The wrapper only needs the cursor hint.
      const style: CSSProperties = {
        cursor: docId ? 'pointer' : 'default',
      };

      // Reference chip on a settlement card. In 1:1 rendering this is
      // ``group.deliveryEvent`` (the single LS in the pair). In 1:N
      // rendering each GS card receives its OWN target LS via
      // ``referencesDelivery`` so a stacked card shows the document it
      // actually cites — not whichever LS happens to be lex-smallest.
      //
      // #781 fallback: on orphan settlements (no resolved
      // ``refDelivery``) the GS entity itself still carries the cited
      // LS number on its ``delivery_reference`` attribute (extracted
      // from the GS letterhead at ingestion). Synthesise the chip from
      // that string so the operator keeps the lead — they need
      // ``LF20250222`` to go hunt for the missing LS in the source
      // filesystem; today's UI dropped it on the floor. No date is
      // available (the LS doesn't exist), so the chip omits the date
      // separator. Locality-preserving: no extra fetch — the
      // attribute is already on the loaded event entity.
      const refDelivery =
        referencesDelivery ??
        (cardIndex === 0 && isPaired(group) ? group.deliveryEvent : null);
      const orphanRefNumber =
        side === 'settlement' && !refDelivery
          ? // #3030 — may be an array (the wibill lane's N-Lieferscheine-per-invoice
            // shape). ``?? undefined`` keeps the "cites nothing" branch identical.
            (deliveryReferenceLabel(resolvedEvent) ?? undefined)
          : undefined;
      const referenceTo =
        side === 'settlement' && refDelivery
          ? {
              docNumber: String(
                refDelivery.delivery_number ??
                  refDelivery.delivery_note_number ??
                  refDelivery.entity_id ??
                  '—',
              ),
              date: formatDate(
                (refDelivery.event_date ?? refDelivery.delivery_date) as string | undefined,
              ),
            }
          : orphanRefNumber
            ? { docNumber: orphanRefNumber }
            : undefined;

      // #781 POS chip — surfaces the stable per-event POS-token the
      // operator uses to cross-reference between GS and LS sides.
      // The linker writes it under different attribute names per
      // subclass (settlement_event.reference vs.
      // delivery_event.pos_reference) — ``eventPosToken`` is the
      // single source of truth for the field-name divergence so the
      // layout-pass height calculation reads the same value as the
      // prop derivation. Independent of pairing status (orphan
      // tiles surface POS too, so #783's back-edge has a stable
      // reference).
      const posNumber = eventPosToken(side, resolvedEvent);

      // #833 — LS-side BestellNr subtitle. ``delivery_event.bestell_number``
      // is the extracted Bestell-Nr the LS cites (per #784); surfacing
      // it on the tile + anchoring the Bestellung←LS arrow at its
      // subtitle row makes the "why this BestellNr?" link visible to
      // the operator. Settlement side never sets this prop — the GS
      // tile has its own ``Ref: Lieferschein #…`` subtitle.
      const bestellNumber =
        side === 'delivery'
          ? typeof resolvedEvent.bestell_number === 'string' && resolvedEvent.bestell_number.length > 0
            ? (resolvedEvent.bestell_number as string)
            : undefined
          : undefined;

      const docNumber =
        side === 'delivery'
          ? String(resolvedEvent.delivery_number ?? resolvedEvent.delivery_note_number ?? resolvedEvent.entity_id ?? '—')
          : String(resolvedEvent.number ?? resolvedEvent.credit_note_number ?? resolvedEvent.settlement_number ?? resolvedEvent.entity_id ?? '—');

      // Left-arrow-right convention reads as the document does. For a
      // Lieferschein that's the *goods* flow (sender → recipient =
      // farmer → processor). For a Gutschrift it's the *document* flow
      // (issuer → recipient = processor → farmer) — the PDF puts the
      // issuer in the letterhead and the recipient in the address
      // block, so we match that. In ontology terms the Gutschrift's
      // ``buyer_name`` is the issuer (Aussteller / provider_id, e.g.
      // FRUTANIA) and ``seller_name`` is the beneficiary (Empfänger /
      // receiver_id, e.g. Ulrich Bonn). Flipping these here is purely
      // a presentation change — the underlying entity is unchanged.
      const supplierName =
        side === 'delivery'
          ? String(resolvedEvent.sender_name ?? resolvedEvent.name ?? '—')
          : String(resolvedEvent.buyer_name ?? resolvedEvent.name ?? '—');
      const buyerName =
        side === 'delivery'
          ? String(resolvedEvent.recipient_name ?? resolvedEvent.buyer_name ?? '')
          : String(resolvedEvent.seller_name ?? '');

      // Tiles that carry the kebab + "Als erledigt markieren" action:
      //   - orphan_settlement → settlement tile only (no delivery to mark)
      //   - orphan_delivery   → delivery tile only (no settlement to mark)
      //   - partial (Abweichung) → BOTH sides, symmetric
      // Matched paired tiles have no actions and no kebab — there's
      // nothing to triage.
      // Each tile writes to ITS OWN entity's ``manually_resolved_at``
      // so a partial pair can be marked from either side independently
      // (operator dismissing the Abweichung from whichever document
      // they happen to have under inspection). The dashboard counter
      // excludes the partial group once *either* side is resolved
      // (see ``FilterBar``'s ``isOperatorResolved``).
      const isActionableSide =
        (side === 'settlement' && group.status === 'orphan_settlement') ||
        (side === 'delivery' && group.status === 'orphan_delivery') ||
        group.status === 'partial';
      const tileEntityId = isActionableSide ? String(resolvedEvent.entity_id ?? '') : '';
      const entityType: 'settlement_event' | 'delivery_event' =
        side === 'settlement' ? 'settlement_event' : 'delivery_event';

      // Per-tile resolved state: read this side's own
      // ``manually_resolved_at`` directly off the raw event entity
      // (rather than the group-level ``manuallyResolvedAt`` which
      // collapses both sides into one). The override map wins so the
      // tile flips instantly when the operator clicks; the persisted
      // attribute is the source of truth on reload.
      const override = tileEntityId ? resolvedOverrides?.get(tileEntityId) : undefined;
      const rawResolvedAt = (resolvedEvent as Record<string, unknown>).manually_resolved_at;
      const persistedResolvedAt =
        typeof rawResolvedAt === 'string' && rawResolvedAt.length > 0;
      const isResolved = isActionableSide
        ? override !== undefined
          ? override
          : persistedResolvedAt
        : false;

      // #1395 — per-tile suppression state, read off THIS event's own
      // ``suppressed_at`` with the optimistic override winning (same
      // precedence as the resolved path above). Suppression is offered
      // on every actionable tile — an erroneous QK / Papierbestellung
      // can surface on either side of any non-matched group.
      const suppressOverride = tileEntityId ? suppressOverrides?.get(tileEntityId) : undefined;
      const rawSuppressedAt = (resolvedEvent as Record<string, unknown>).suppressed_at;
      const persistedSuppressed =
        typeof rawSuppressedAt === 'string' && rawSuppressedAt.length > 0;
      const isSuppressed = isActionableSide
        ? suppressOverride !== undefined
          ? suppressOverride
          : persistedSuppressed
        : false;

      // #1396 / #1442 — per-tile flag + comment state, read off THIS
      // event's own ``flagged_at`` / ``flag_comment`` with the optimistic
      // overrides winning (same precedence as resolve / suppress above).
      // UNLIKE suppression the flag is NON-destructive — flagging never
      // removes the tile, it only adds a header marker (+ a feed note for
      // the end-user). #1442: the flag MARKER + its edit/remove kebab
      // actions must survive a rematerialise to ``matched`` (a flagged
      // orphan/partial that later pairs up), so the flag path keys off a
      // flag-scoped entity id read straight off the event — independent of
      // ``isActionableSide``. Only the FRESH-flag action stays actionable-
      // only. Flag + suppress are orthogonal: a tile can be both, so the
      // flag action stays available even when suppressed.
      const flagState = buildLineRowFlagState({
        event: resolvedEvent as Record<string, unknown>,
        flagOverrides,
        flagCommentOverrides,
        isActionableSide,
        entityType,
        onFlag,
        onUnflag,
      });
      const { isFlagged, flagComment } = flagState;

      const actions: LineRowNodeData['actions'] = [];
      if (isActionableSide && tileEntityId) {
        // Resolution actions are hidden while a tile is suppressed — a
        // suppressed doc is out of the reconciliation, so "als erledigt"
        // has no meaning until it's re-shown.
        if (!isSuppressed) {
          if (isResolved && onUnmarkResolved) {
            actions.push({
              label: 'Erledigung rückgängig machen',
              onClick: () => onUnmarkResolved(entityType, tileEntityId),
            });
          } else if (!isResolved && onMarkResolved) {
            actions.push({
              label: 'Als erledigt markieren',
              onClick: () => onMarkResolved(entityType, tileEntityId),
            });
          }
        }
        // #1395 — suppress / un-suppress (reversal) action. First in the
        // kebab chain per the subtask; ordered after the resolve action
        // when both are present so the destructive option isn't the
        // default landing target.
        if (isSuppressed && onUnsuppress) {
          actions.push({
            label: 'Wieder einblenden',
            onClick: () => onUnsuppress(entityType, tileEntityId),
          });
        } else if (!isSuppressed && onSuppress) {
          actions.push({
            label: 'Beleg ausblenden',
            onClick: () => onSuppress(entityType, tileEntityId),
          });
        }
      }
      // #1396 / #1442 — flag + comment kebab actions. Appended AFTER the
      // resolve/suppress block so the kebab order on an actionable tile is
      // unchanged (resolve, suppress, then flag). Crucially these live
      // OUTSIDE the ``isActionableSide`` guard: ``buildLineRowFlagState``
      // already scopes edit/remove to "a flag exists" (any tile) and the
      // fresh-flag to "actionable + un-flagged", so a persisted flag on a
      // rematerialised ``matched`` tile keeps its edit/remove kebab (#1442).
      if (flagState.flagActions) {
        actions.push(...flagState.flagActions);
      }

      // Node id encodes the card index so multi-event groups produce
      // unique ids per card without breaking single-event id stability
      // (``de_<group_id>`` / ``se_<group_id>`` for cardIndex 0).
      const nodeId =
        cardIndex === 0
          ? `${side === 'delivery' ? 'de' : 'se'}_${group.id}`
          : `${side === 'delivery' ? 'de' : 'se'}_${group.id}_${cardIndex}`;
      // #3237 — register the card for the invalid-drop classifier.
      cardSideMap.set(nodeId, { side, groupId: group.id });
      // Primary-tile rule (mirrors the sub-11 materialiser's primary
      // entity choice): settlement side wins when present; otherwise
      // the delivery side carries the per-group test id. One testid
      // per group regardless of pairing — matched groups don't double
      // up just because they render two tiles. In multi-event groups,
      // only the FIRST card on the primary side carries the testid.
      const isPrimaryTile =
        cardIndex === 0 &&
        (side === 'settlement'
          ? !!group.settlementEvent
          : !group.settlementEvent);
      const groupTestId = isPrimaryTile
        ? `match-group-tile-${group.id}`
        : undefined;
      const node: Node = {
        id: nodeId,
        type: 'lineRow',
        position: { x, y },
        data: {
          side,
          docNumber,
          // #839 — LS header pins to ``document_date`` (issue date),
          // matching GS / Bestellung conventions. Legacy LS rows
          // (extracted before the schema split) fall back to the old
          // ``event_date`` / ``delivery_date`` chain so they keep
          // displaying *some* date instead of "—".
          date: formatDate(headerDateValue(side, resolvedEvent as Record<string, unknown>)),
          supplierName,
          buyerName,
          lines: sortedLines,
          referenceTo,
          posNumber,
          // #839 — Liefertermin subtitle chip. LS-side only; suppressed
          // when ``document_date`` is missing (legacy row) or when the
          // Liefertermin would just repeat the header date.
          liefertermin: lieferterminChipValue(side, resolvedEvent as Record<string, unknown>),
          bestellNumber,
          sourceDocId: docId,
          isActive: active,
          duplicateCount: group.duplicateCount,
          isResolved,
          // #1395 — suppressed tiles stay on the data-administration
          // canvas (muted) so ops can see + reverse the suppression.
          isSuppressed,
          // #1396 — flag marker + the reviewer's note (tooltip). Non-
          // destructive: rendered without dimming, the tile stays in
          // every view.
          isFlagged,
          flagComment,
          actions: actions.length > 0 ? actions : undefined,
          groupTestId,
        } satisfies LineRowNodeData,
        style,
      };
      return { node, nodeId, docId };
    };

    // Same visibility filter LineRow applies: when any line carries a
    // ``resource_category`` the tile only renders goods rows; everything
    // else folds into the footer chip strip. Stubs / edges must obey
    // the same filter or they'll float below the rendered rows.
    const isVisibleLine = (l: MatchGroupLine, all: MatchGroupLine[]) => {
      const everyUntagged = all.every((x) => !x.resourceCategory);
      return everyUntagged || l.resourceCategory === 'goods';
    };

    // ── Paired groups (top section) ───────────────────────────────────────
    //
    // Multi-event groups (1:N where one LS receives several GS, or the
    // mirror with one GS spanning several LS) render N cards per
    // affected side. Each card carries only its OWN event's lines so
    // the reviewer sees one tile per real document. Line-match edges
    // resolve through ``lineId → nodeId`` maps so a matched pair finds
    // its target card even when the GS-side has multiple stacks.
    //
    // Layout: independent vertical stacks on each column, top-aligned
    // at the same group y. The first card of each stack respects
    // ``REF_ROW_PUSH`` (LS shifted down so its title row aligns with
    // the first GS's Ref row). After both stacks render, the group
    // advances by the taller stack's height.
    for (const group of pairedGroups) {
      // #844 — open a fresh grid row for this group. Each column slot
      // stays ``null`` until a real tile is pushed for it below; the
      // arrow-key reducer treats a ``null`` cell as "skip".
      const gridRowIndex = gridRows.length;
      const gridRow = newRow();
      gridRows.push(gridRow);
      // Resolve the per-side event arrays. ``allSettlementEvents`` and
      // ``allDeliveryEvents`` (added in sub-13 follow-up for the
      // multi-GS dashboard math) contain every event in the component.
      // Fall back to the primary singletons when those arrays are empty
      // — e.g. legacy facts that predate the field.
      const seEvents: Record<string, unknown>[] =
        group.allSettlementEvents.length > 0
          ? group.allSettlementEvents
          : group.settlementEvent
            ? [group.settlementEvent as Record<string, unknown>]
            : [];
      const deEvents: Record<string, unknown>[] =
        group.allDeliveryEvents.length > 0
          ? group.allDeliveryEvents
          : group.deliveryEvent
            ? [group.deliveryEvent as Record<string, unknown>]
            : [];

      // Bucket lines by their parent event so each card receives only
      // its own document's rows.
      const seLinesByEvent = new Map<string, MatchGroupLine[]>();
      for (const sl of group.settlementLines) {
        const arr = seLinesByEvent.get(sl.eventId) ?? [];
        arr.push(sl);
        seLinesByEvent.set(sl.eventId, arr);
      }
      const deLinesByEvent = new Map<string, MatchGroupLine[]>();
      for (const dl of group.deliveryLines) {
        const arr = deLinesByEvent.get(dl.eventId) ?? [];
        arr.push(dl);
        deLinesByEvent.set(dl.eventId, arr);
      }

      // Build delivery cards first so settlement cards can look up
      // their REF target by ``delivery_number``.
      interface RenderedCard {
        node: Node;
        nodeId: string;
        y: number;
        event: Record<string, unknown>;
        lines: MatchGroupLine[];     // pre-sort, post-eventId-bucket
        visibleLines: MatchGroupLine[]; // sorted + visible-filtered
      }

      // #842 — pre-compute the per-column stack heights so we can
      // vertically center each column inside the row. Without this,
      // a tall multi-line GS pulls its column down while shorter
      // siblings (Bestellung / Abrechnung / Zahlungsavis at the
      // ~70px tile height) hug the row top with a large empty band
      // below — canary: Wiesenhof April 2026 ``Ohne Zuordnung``.
      //
      // Heights are the bounding-box size of each column's stack
      // (sum of tile heights + STACK_GAP between tiles). The LS+GS
      // pair is a single combined "lsgs" column whose effective
      // height accounts for ``REF_ROW_PUSH`` (the LS shift below the
      // GS top that keeps the REF arrow horizontal). All shifts
      // apply equally to LS and GS so that arrow geometry is
      // preserved as the pair slides down together.
      //
      // #1123 — heights are sourced from xyflow v12's measured DOM
      // via ``measuredHeights`` (populated by ``useMeasuredHeights``).
      // ``baselineTileHeight`` is the first-frame fallback before the
      // ResizeObserver fires; it does NOT account for subtitle rows
      // (those were the recurring source of the spacing drift). See
      // ``stackHeight`` for the formula and
      // ``__tests__/stackYs.test.ts`` for the regression-killer.
      //
      // Build node-id arrays up-front (the same id derivation
      // ``buildLineRowNode`` uses below — ``${side}_${group.id}`` for
      // cardIndex 0, ``${side}_${group.id}_${idx}`` otherwise) so the
      // measured-height lookup resolves before we emit the cards.
      const deCardIds = deEvents.map((_, idx) =>
        idx === 0 ? `de_${group.id}` : `de_${group.id}_${idx}`,
      );
      const seCardIds = seEvents.map((_, idx) =>
        idx === 0 ? `se_${group.id}` : `se_${group.id}_${idx}`,
      );
      const deBaselineHeights = deEvents.map((deEvent) => {
        const lines = deLinesByEvent.get(String(deEvent.entity_id ?? '')) ?? [];
        return baselineTileHeight(lines);
      });
      const seBaselineHeights = seEvents.map((seEvent) => {
        const lines = seLinesByEvent.get(String(seEvent.entity_id ?? '')) ?? [];
        return baselineTileHeight(lines);
      });
      const deStackH = stackHeight({
        cardIds: deCardIds,
        baselineHeights: deBaselineHeights,
        measuredHeights,
        stackGap: STACK_GAP,
      });
      const seStackH = stackHeight({
        cardIds: seCardIds,
        baselineHeights: seBaselineHeights,
        measuredHeights,
        stackGap: STACK_GAP,
      });
      // Bestellung tiles are fixed-height by design
      // (``BESTELLUNG_TILE_HEIGHT = 70``) and the placement loop
      // below still advances by that constant. Compute the envelope
      // from the same fixed-baseline arithmetic so envelope-math
      // and placement-math cannot diverge — passing the live
      // ``measuredHeights`` here would let an off-baseline measurement
      // (e.g. font load shifting the row by 1-2 px) collapse or
      // enlarge the visible gap after the last Bestellung tile, since
      // the LS offset is derived from the envelope but each tile's
      // y comes from the fixed constant. (Codex round 1 P2.)
      const bestellungH = stackHeight({
        cardIds: group.bestellung.map((_, idx) => `bestellung_${group.id}_${idx}`),
        baselineHeights: group.bestellung.map(() => BESTELLUNG_TILE_HEIGHT),
        measuredHeights: EMPTY_MEASURED_HEIGHTS,
        stackGap: STACK_GAP,
      });
      // #915 — Bestellung tile(s) now stack ABOVE the LS in the same
      // left column. ``bestellungLsgsOffset`` pushes the LS+GS pair
      // down so the visible gap between the last Bestellung tile and
      // the LS tile is exactly STACK_GAP. We subtract REF_ROW_PUSH
      // because ``computePairedLsgsYs`` re-adds it to ``deY`` (LS y);
      // without the subtraction the LS would sit ``STACK_GAP +
      // REF_ROW_PUSH`` below Bestellung-bottom (codex round 2 P3).
      // The GS y sits at ``lsgsTop`` (= ``LS_y - REF_ROW_PUSH``); GS
      // lives in COL_RIGHT, so it freely overlaps Bestellung's y range
      // without visual collision. When ``group.bestellung`` is empty
      // the offset collapses to 0 and the layout is bit-for-bit
      // identical to pre-#915.
      const bestellungLsgsOffset =
        bestellungH > 0 ? bestellungH + STACK_GAP - REF_ROW_PUSH : 0;
      const lsgsH =
        bestellungLsgsOffset + Math.max(seStackH, REF_ROW_PUSH + deStackH);
      // #917 — Abrechnung column no longer participates in row centering;
      // each tile top-aligns to its citing GS, computed inside
      // ``buildPayoutColumns``. The pre-emission ``computeRowLayout``
      // envelope only needs ``lsgs`` and ``avis``. The post-emission
      // fold below at the ``yOffset`` advance covers any case where
      // the Abrechnung depth exceeds ``layout.rowH``. Zahlungsavis is
      // real-only — placeholders not emitted.
      // #1123: Zahlungsavis tiles are fixed-height too (mirrors
      // ``BESTELLUNG_TILE_HEIGHT``) and ``buildPayoutColumns``
      // positions them with the same constant. Compute the envelope
      // from the same fixed baseline — see the Bestellung block
      // above for why the live measured map MUST NOT flow into a
      // fixed-position stack (codex round 1 P2).
      const avisH = stackHeight({
        cardIds: group.payoutAdvices.map((_, idx) => `payout_advice_${group.id}_${idx}`),
        baselineHeights: group.payoutAdvices.map(() => BESTELLUNG_TILE_HEIGHT),
        measuredHeights: EMPTY_MEASURED_HEIGHTS,
        stackGap: STACK_GAP,
      });
      const layout = computeRowLayout({
        lsgs: lsgsH,
        avis: avisH,
      });

      // #914 — split the combined ``lsgs`` envelope into per-side y
      // starts. When the LS stack dominates (``REF_ROW_PUSH + deStackH >
      // seStackH``) the GS centers inside the LS envelope instead of
      // pinning to the row top, killing the empty band that pre-#914
      // inflated the GS-to-next-GS gap on multi-LS rows. When the GS
      // dominates or a single LS sits opposite a single GS, the helper
      // collapses to the pre-#914 output bit-for-bit — single-LS rows
      // are visually unchanged (the #842 baseline). ``lsDominates``
      // also gates the REF arrow's ``coLinear`` flag below.
      //
      // #915 — push the LS+GS pair down by ``bestellungLsgsOffset`` so
      // the Bestellung stack sits at the top of the merged left-column
      // envelope. The offset already accounts for the REF_ROW_PUSH
      // that ``computePairedLsgsYs`` re-adds to ``deY``, so the
      // resulting LS top sits exactly ``STACK_GAP`` below
      // Bestellung's bottom. When the group has no Bestellung the
      // offset is 0 and the LS+GS y starts are unchanged from pre-#915.
      const lsgsTop = yOffset + layout.shifts.lsgs + bestellungLsgsOffset;
      const {
        deY: deY0,
        seY: seY0,
        lsDominates,
      } = computePairedLsgsYs({
        deColH: deStackH,
        seColH: seStackH,
        lsgsTop,
        refRowPush: REF_ROW_PUSH,
      });

      // #1123 — per-card y-positions via ``stackYs``. Reads measured
      // heights for each card from xyflow's nodeLookup; falls back to
      // ``baselineTileHeight`` only on the first paint before
      // measurement. Replaces the pre-#1123 ``deY += nodeHeight(...,
      // hasPosRow, hasBestellRow, hasLieferterminRow) + STACK_GAP``
      // loop, whose hand-rolled subtitle-row terms were the recurring
      // source of canvas spacing drift across #781/#833/#888-#892/#914.
      const deYs = stackYs({
        cardIds: deCardIds,
        baselineHeights: deBaselineHeights,
        measuredHeights,
        startY: deY0,
        stackGap: STACK_GAP,
      });
      const deCards: RenderedCard[] = [];
      deEvents.forEach((deEvent, idx) => {
        const eid = String(deEvent.entity_id ?? '');
        const lines = deLinesByEvent.get(eid) ?? [];
        const sortedLines = sortLines(lines);
        const visibleLines = sortedLines.filter((l) => isVisibleLine(l, sortedLines));
        const cardY = deYs[idx];
        const built = buildLineRowNode(
          group,
          'delivery',
          COL_LEFT,
          cardY,
          deEvent,
          lines,
          idx,
        );
        if (built.node) {
          nodes.push(built.node);
          deCards.push({
            node: built.node,
            nodeId: built.nodeId,
            y: cardY,
            event: deEvent,
            lines: sortedLines,
            visibleLines,
          });
          // #844 — record FIRST LS card per row as the row's
          // ``lieferschein`` cell. Subsequent stacked LS cards within
          // the same group share the row but only the topmost tile
          // is reachable via arrow keys (otherwise a vertical stack
          // of LS cards would expose multiple cells on the same row
          // for the same column — degenerate for the reducer).
          if (gridRow.lieferschein == null) {
            gridRow.lieferschein = tileRef(
              'lieferschein',
              gridRowIndex,
              built.nodeId,
              built.docId,
            );
          }
        }
      });

      // Index of LS cards by ``delivery_number`` so each settlement
      // card can look up the LS it references and draw its REF arrow
      // there (rather than always at the lex-smallest LS, which would
      // be wrong for the N:1 mirror case).
      const deCardByNumber = new Map<string, RenderedCard>();
      for (const c of deCards) {
        const num = String(
          c.event.delivery_number ?? c.event.delivery_note_number ?? '',
        );
        if (num) deCardByNumber.set(num, c);
      }

      // #1123 — symmetric measured-layout pass for GS cards. Replaces
      // the pre-#1123 ``seY += nodeHeight(...) + STACK_GAP`` loop.
      const seYs = stackYs({
        cardIds: seCardIds,
        baselineHeights: seBaselineHeights,
        measuredHeights,
        startY: seY0,
        stackGap: STACK_GAP,
      });
      const seCards: RenderedCard[] = [];
      seEvents.forEach((seEvent, idx) => {
        const eid = String(seEvent.entity_id ?? '');
        const lines = seLinesByEvent.get(eid) ?? [];
        const sortedLines = sortLines(lines);
        const visibleLines = sortedLines.filter((l) => isVisibleLine(l, sortedLines));
        // Pick the LS card this GS references. ``delivery_reference``
        // is the GS-side citation of an LS document number. Fall back
        // to the first LS card when no match is found (defensive — a
        // multi-GS component implies every GS *should* reference an LS
        // in the same component, but mid-pipeline runs may not yet
        // have the field populated).
        //
        // #3030 — the value may be an ARRAY (one wibill invoice settles N
        // Lieferscheine). ``String(['LF1','LF2'])`` is ``'LF1,LF2'``, which matches no
        // card, so this silently took the ``deCards[0]`` fallback and anchored the arrow
        // to whichever Lieferschein sorted first — a WRONG document, indistinguishable
        // from the legitimate "field not populated yet" case. Try each cited reference
        // and take the first that actually names a card in this component.
        const ref = firstResolvableReference(seEvent, (candidate) => deCardByNumber.has(candidate));
        const targetLs = (ref && deCardByNumber.get(ref)) || deCards[0] || null;
        const cardY = seYs[idx];
        const built = buildLineRowNode(
          group,
          'settlement',
          COL_RIGHT,
          cardY,
          seEvent,
          lines,
          idx,
          targetLs ? targetLs.event : null,
        );
        if (built.node) {
          nodes.push(built.node);
          seCards.push({
            node: built.node,
            nodeId: built.nodeId,
            y: cardY,
            event: seEvent,
            lines: sortedLines,
            visibleLines,
          });
          // #844 — record FIRST GS card per row as the ``gutschrift``
          // cell (see lieferschein note above for why first-only).
          if (gridRow.gutschrift == null) {
            gridRow.gutschrift = tileRef(
              'gutschrift',
              gridRowIndex,
              built.nodeId,
              built.docId,
            );
          }
          // Dotted grey REF arrow: this GS's Referenz → its target LS
          // title. Two routing modes:
          //   - co-linear (this GS card is cardIndex 0 AND its target
          //     LS is cardIndex 0 AND the GS sits at the LS-top y) →
          //     ``straight``. The ``REF_ROW_PUSH`` shift puts the two
          //     handles at the same canvas y; a curved router would
          //     draw an unneeded S-bend through co-linear endpoints.
          //   - non-co-linear (any subsequent GS card in a 1:N stack
          //     pointing back at the shared LS at the top, OR any
          //     paired row where #914 centered the GS down inside an
          //     LS-dominant envelope) → ``smoothstep``. The arrow
          //     routes right→up→left in clean orthogonals with
          //     rounded corners instead of cutting diagonally across
          //     the canvas — fixes the "jagged GS #999959 → LF99990014
          //     arrow" reading in the 1:N canary screenshot.
          if (targetLs) {
            // #843 — routing flag selects between the straight + smoothstep
            // modes of the shared primitive. Co-linear (cardIndex 0 →
            // cardIndex 0): straight, since ``REF_ROW_PUSH`` puts the
            // handles at the same canvas y. Multi-event (non-co-linear):
            // smoothstep with rounded corners. ``offset: 20`` is the
            // half-gap between LS card right edge (``COL_LEFT + TILE_WIDTH
            // = 400``) and GS card left edge (``COL_RIGHT = 440``): with
            // offset = 20, the source-side and target-side bend points
            // both land at x = 420 → the smoothstep "Z" collapses into a
            // clean single-segment L (otherwise the algorithm inserts a
            // y-midpoint detour that reads as a "broken" vertical).
            //
            // #914 — ``lsDominates`` extra gate: once the LS stack
            // pulls the GS card down to the envelope midpoint, the
            // GS ``referenz-source`` and LS ``title-target`` handles
            // no longer share a canvas y. Holding ``coLinear: true``
            // here would force ``straight`` and draw a literal slanted
            // segment; switching to smoothstep produces the proper
            // orthogonal Z routing the issue calls out as acceptable.
            const coLinear = !lsDominates && idx === 0 && targetLs === deCards[0];
            edges.push(
              // HOF-219 — the ONE dashed edge that carries a document unlink.
              buildDocumentRefEdge({
                id: `ref_${group.id}_${idx}`,
                source: built.nodeId,
                sourceHandle: 'referenz-source',
                target: targetLs.nodeId,
                targetHandle: 'title-target',
                coLinear,
                settlementEvent: seEvent,
                deliveryEvent: targetLs.event,
                groupId: group.id,
                // ``null`` when ``gs_ls_edges`` does not name this pair — which
                // is reachable, because the arrow above is anchored by
                // ``delivery_reference`` and falls back to the first LS card.
                // The confirmation says "unbekannt" there rather than guessing.
                linkKind: resolveDocumentLinkKind(
                  group.gsLsEdges ?? [],
                  String(seEvent.entity_id ?? ''),
                  String(targetLs.event.entity_id ?? ''),
                ),
                // #1174 — this REF arrow (lower source top: 44 → the LS
                // BELOW) is the LOWER half of the GS-leaving pair. Bend
                // LATE/LEFT (near the LS) so it stays low across the gap
                // and clears the Bestellung arrow, which bends early/right.
                // Only the routed (non-coLinear) case shares the gap with
                // the Bestellung arrow; coLinear is already a straight
                // horizontal and can't cross anything.
                ...(coLinear
                  ? {}
                  : {
                      offset: GS_LEAVING_EDGE_OFFSET,
                      stepPosition: GS_REF_STEP_POSITION,
                    }),
              }),
            );
          }
        }
      });

      // ── BESTELLUNG sub-cell of the LS column (#784, collapsed by #915) ──
      // Render one tile per hydrated ``sales_agreement`` STACKED ABOVE
      // the LS tile at x = COL_LEFT. Multiple agreements stack
      // vertically (Bestellung[0] at the top, Bestellung[N-1] just
      // above the first LS tile). Each tile receives a dotted incoming
      // edge from the card whose event actually cites it
      // (``event.agreement_id === bestellung.entityId``). When
      // ``group.bestellung`` is empty, no tile renders and the LS y
      // starts pre-#915 are preserved bit-for-bit.
      let bestellungY = yOffset + layout.shifts.lsgs;
      // #1176 — GS card top-y lookup (nodeId → canvas y). When a
      // Bestellung placeholder is GS-sourced, its y follows the citing
      // GS card's row instead of the sequential ``bestellungY`` stack so
      // one-LS→many-GS groups line each placeholder up with its GS
      // partner (the sequential counter only aligned in the 1:1 case).
      const seCardYById = new Map(seCards.map((c) => [c.nodeId, c.y]));
      // #1176 — y's already claimed by prior GS-sourced placeholders in
      // this group; threaded into ``computeBestellungPlaceholderY`` so
      // two agreements citing the same GS row don't overlap (small
      // step-down fallback per the collision guard).
      const usedBestellungGsYs = new Set<number>();
      // #1176 (codex P2) — vertical extent each LS card occupies in
      // COL_LEFT (the placeholder's own column). A GS-sourced placeholder
      // aligned to the FIRST GS row would land ``REF_ROW_PUSH`` above —
      // i.e. on top of — its paired LS; ``computeBestellungPlaceholderY``
      // uses these spans to fall back to the sequential above-LS slot in
      // that case. Heights mirror ``stackYs``'s measured>baseline policy.
      const lsColumnSpans = deCards.map((c) => {
        const measured = measuredHeights.get(c.nodeId);
        const h =
          typeof measured === 'number' ? measured : baselineTileHeight(c.lines);
        return { top: c.y, bottom: c.y + h };
      });
      group.bestellung.forEach((b, idx) => {
        const nodeId = `bestellung_${group.id}_${idx}`;
        // #832 — sparse agreement (BestellNr cited by an upstream LS/GS
        // but no real Bestellung document ever ingested) renders as a
        // missing-doc placeholder instead of a real Bestellung tile.
        // Heuristic: no parties extracted AND no descriptive name
        // distinct from the BestellNr. This is the "Bestellung
        // #1273425 ... 1273425" duplication path from the bug
        // report — the materialiser hydrated only the agreementNumber
        // from a reference, so a real-tile render shows the BestellNr
        // in the header AND a duplicate in the date slot. Routing
        // these through MissingDocNode replaces both with one
        // ``Bestellung fehlt`` title + ``#<BestellNr>`` subtitle.
        // (#1812 — computed BEFORE the nav-grid registration below so a
        // sparse placeholder never registers a document docId.)
        const isSparsePlaceholder =
          b.supplierName === null &&
          b.buyerName === null &&
          (b.name === null || (b.agreementNumber !== null && b.name === b.agreementNumber));
        // #844 + #1812 — first Bestellung tile in this group becomes the
        // row's ``bestellung`` cell. A REAL Bestellung tile now carries
        // the singular ``source_doc_id`` provenance (subtask 07), so
        // keyboard nav to it drives the preview panel just like clicking
        // it — a consistent docId path across mouse + keyboard. A
        // stub-minted agreement (no provenance) OR a sparse placeholder
        // (rendered as a ``Bestellung fehlt`` missing-doc tile, which the
        // mouse click-gate treats as inert) keeps ``docId: null`` so the
        // tile is focusable but shows no preview — keeping mouse and
        // keyboard consistent for every tile shape.
        if (idx === 0) {
          gridRow.bestellung = tileRef(
            'bestellung',
            gridRowIndex,
            nodeId,
            isSparsePlaceholder ? null : b.sourceDocId ?? null,
          );
        }
        // #1176 — resolve the dashed-arrow source BEFORE positioning the
        // tile (pre-#1176 ``picked`` was computed after the node push).
        // ``picked.sourceHandle`` tells us whether the citing card is an
        // LS (``bestellung-source``) or a GS (``bestellung-from-gs``);
        // the GS-sourced tile aligns to its GS row rather than the
        // sequential top stack. See the preference-order note below.
        const picked = pickBestellungSourceCard(deCards, seCards, {
          entityId: b.entityId,
          agreementNumber: b.agreementNumber,
        });
        // #1176 — GS-sourced placeholder ⇒ align y to the citing GS
        // card's row (so the dashed Bestellung→GS edge reads straight
        // and the tile lines up with its partner in the one-LS→many-GS
        // fan-out). LS-sourced (or an unresolved/missing GS y) keeps the
        // sequential ``bestellungY`` left-column top stack. The collision
        // guard nudges a second placeholder off an already-claimed GS y.
        //
        // Gate on ``seCards.length > 1`` — the fan-out is the ONLY case
        // that misbehaves. With a single GS the sequential top stack
        // already reads cleanly (the shipped #784/#915 1:1 layout where
        // the placeholder sits above the LS, ``bestellungLsgsOffset``
        // above the GS top); re-anchoring it to ``seCard.y`` there would
        // shift the lone placeholder down by that offset for no benefit
        // and trip the "1:1 unchanged" acceptance gate. So single-GS
        // groups keep the pre-#1176 sequential y bit-for-bit.
        const pickedGsRowY =
          seCards.length > 1 &&
          picked &&
          picked.sourceHandle === 'bestellung-from-gs'
            ? (seCardYById.get(picked.sourceCard.nodeId) ?? null)
            : null;
        const placement = computeBestellungPlaceholderY({
          sequentialY: bestellungY,
          gsRowY: pickedGsRowY,
          usedYs: usedBestellungGsYs,
          collisionStep: BESTELLUNG_TILE_HEIGHT + STACK_GAP,
          lsSpans: lsColumnSpans,
          tileHeight: BESTELLUNG_TILE_HEIGHT,
        });
        const placeholderY = placement.y;
        // Reserve the y ONLY when the tile actually took its GS row — an
        // LS-overlap fallback returns the sequential y, which the next
        // iteration's own ``bestellungY`` already accounts for.
        if (placement.usedGsRow) usedBestellungGsYs.add(placeholderY);
        nodes.push(
          isSparsePlaceholder
            ? {
                id: nodeId,
                type: 'missingDoc',
                position: { x: COL_LEFT, y: placeholderY },
                data: {
                  missingSide: 'bestellung',
                  width: TILE_WIDTH,
                  height: BESTELLUNG_TILE_HEIGHT,
                  expectedNumber: b.agreementNumber,
                } satisfies MissingDocNodeData,
                draggable: false,
                selectable: false,
              }
            : {
                id: nodeId,
                type: 'bestellung',
                position: { x: COL_LEFT, y: placeholderY },
                data: {
                  entityId: b.entityId,
                  agreementNumber: b.agreementNumber,
                  name: b.name,
                  supplierName: b.supplierName,
                  buyerName: b.buyerName,
                  // #1812 — order-doc provenance drives the tile's click
                  // affordance + preview; null ⇒ non-clickable tile.
                  sourceDocId: b.sourceDocId,
                } satisfies BestellungNodeData,
              },
        );
        // ``smoothstep`` because BESTELLUNG x is far to the left and the
        // y typically differs from the source card y — a straight line
        // would diagonally cut across LS tiles. Matches the existing
        // GS→LS REF arrow's "smoothstep with rounded corners" routing.
        //
        // #833 — Source-card preference order:
        //   1. LS card whose ``bestell_number`` matches this BestellNr
        //      (the LS now carries the new ``bestellung-source`` handle
        //      on its own subtitle row; arrow originates from the LS
        //      that explicitly cites this agreement — makes the
        //      "why this BestellNr?" link visible on the LS).
        //   2. (#888 removed) Was "LS card whose ``agreement_id``
        //      matches the BESTELLUNG entity". That branch picked LS
        //      cards lacking a ``bestell_number`` string, so LineRow
        //      did not render the ``bestellung-source`` handle and the
        //      edge targeted a missing DOM node — React Flow dropped
        //      it silently. Falling through to the GS branch instead
        //      keeps the arrow visible.
        //   3. GS card citing the agreement by ``agreement_id``
        //      (pre-#833 behaviour — kept as fallback so groups whose
        //      LS hasn't propagated ``bestell_number`` yet still draw
        //      the connecting arrow rather than a floating BESTELLUNG
        //      tile).
        //   4. Lex-smallest seCards[0] (the original final fallback).
        // The picked source dictates the sourceHandle: LS uses
        // ``bestellung-source``, GS uses ``bestellung-from-gs`` (#1048
        // split — was ``referenz-source`` pre-#1048, but sharing that
        // handle with the GS→LS REF arrow produced overlapping
        // smoothstep elbows). See ``pickBestellungSourceCard`` for the
        // extracted + unit-tested implementation. (#1176 — ``picked`` is
        // resolved above, before the tile is positioned, because the
        // GS-vs-LS source decision now also drives the tile's y.)
        if (picked) {
          // #915 — smoothstep handles both routings cleanly: LS-source
          // (Left→Left) routes around the LEFT side of COL_LEFT;
          // GS-source (Left→Right) crosses the LS↔GS column gap as
          // before. No co-linear straight routing needed — the Top→
          // Bottom same-x path that codex round 3 P2 flagged is gone.
          // #1174 — when GS-sourced (``bestellung-from-gs``, upper source
          // top: 22 → the Bestellung tile ABOVE the LS), this is the UPPER
          // half of the GS-leaving pair. Bend EARLY/RIGHT (near GS) with the
          // mirrored stepPosition so its vertical peels up off the gap before
          // the REF arrow's late/left bend — the two route around each other
          // instead of crossing (the residual #1159 left). The LS-sourced
          // case routes Left→Left around the left of the column and never
          // shares the GS→LS gap, so it keeps the default geometry.
          const gsSourced = picked.sourceHandle === 'bestellung-from-gs';
          edges.push(
            buildDashedReferenceEdge({
              id: `bestellung_${group.id}_${idx}_edge`,
              source: picked.sourceCard.nodeId,
              sourceHandle: picked.sourceHandle,
              target: nodeId,
              targetHandle: picked.targetHandle,
              ...(gsSourced
                ? {
                    offset: GS_LEAVING_EDGE_OFFSET,
                    stepPosition: GS_BESTELLUNG_STEP_POSITION,
                  }
                : {}),
            }),
          );
        }
        bestellungY += BESTELLUNG_TILE_HEIGHT + STACK_GAP;
      });

      // ── ABRECHNUNG + ZAHLUNGSAVIS columns (#793 phase 4) ─────────
      // Delegated to ``buildPayoutColumns`` so the orphan_settlement
      // branch (which also has a GS card that can cite an
      // abrechnungs_number or be referenced by a Zahlungsavis) can
      // emit the same tiles without duplicating the closure. Strict
      // placeholder rule for Abrechnung (§A.7) and real-only for
      // Zahlungsavis (§E.5) both live inside the helper.
      const payoutEmission = buildPayoutColumns(group, {
        // #917 — thread each GS card's top y so Abrechnung tiles can
        // top-align to the citing GS. ``c.y`` is the post-#914 per-GS
        // canvas y captured at ``seCards.push`` above.
        sourceCards: seCards.map((c) => ({ nodeId: c.nodeId, event: c.event, y: c.y })),
        yOffset,
        avisShift: layout.shifts.avis,
      });
      nodes.push(...payoutEmission.nodes);
      edges.push(...payoutEmission.edges);
      // #844 — register the FIRST tile of each payout column with the
      // grid. ``buildPayoutColumns`` emits Abrechnung tiles first
      // (type ``payoutStatement`` or ``missingDoc`` with
      // ``missingSide: 'abrechnung'``) then Zahlungsavis tiles
      // (``payoutAdvice`` — real-only, no placeholder per §E.5). All
      // four tile types carry no source_doc_id on the data shape, so
      // navigation lands on the tile but doesn't drive the preview.
      for (const pn of payoutEmission.nodes) {
        const dataMissingSide = (pn.data as { missingSide?: unknown } | undefined)?.missingSide;
        if (
          pn.type === 'payoutStatement' ||
          (pn.type === 'missingDoc' && dataMissingSide === 'abrechnung')
        ) {
          if (gridRow.abrechnung == null) {
            gridRow.abrechnung = tileRef('abrechnung', gridRowIndex, pn.id, null);
          }
        } else if (pn.type === 'payoutAdvice') {
          if (gridRow.zahlungsavis == null) {
            gridRow.zahlungsavis = tileRef('zahlungsavis', gridRowIndex, pn.id, null);
          }
        }
      }
      // #842 — ``payoutEmission.bottomY`` previously fed the
      // ``Math.max`` groupBottom calculation; the row-layout helper now
      // owns the row envelope (``yOffset + rowH``) so the per-column
      // accumulators no longer need to surface upward.

      // Maps from lineId → (nodeId, visible-row index) per side, so
      // matched-line edges and stub markers know which card carries
      // each line. Visible-row index is needed because LineRow handles
      // sit at visible-row positions, not original-order positions.
      interface LineCoord {
        nodeId: string;
        cardY: number;
        visibleIndex: number; // -1 when the line isn't visible (footer-folded)
        // True when the card renders a POS chip — needed so stub-marker
        // placement adds the same ``POS_ROW_HEIGHT`` reservation that
        // ``nodeHeight`` used during layout (#781 codex P2).
        hasPosRow: boolean;
        // #833 — True when the LS card renders the new BestellNr
        // subtitle row. Stub placement on a LS line below the
        // subtitle must add the same 22px reservation or the dashed
        // mismatch stub points one row above the actual line handle.
        // Settlement-side cards never carry this — the LS subtitle is
        // delivery-only.
        hasBestellRow: boolean;
        // #889 — True when the LS card renders the Liefertermin chip
        // (#839). Same reasoning as ``hasBestellRow``: the stub
        // ``lineYCenter`` call needs the same row reservation that
        // ``nodeHeight`` used during layout, or the dashed mismatch
        // stub points 22 px above the actual line handle on LS cards
        // with a Liefertermin.
        hasLieferterminRow: boolean;
      }
      const seLineCoord = new Map<string, LineCoord>();
      for (const card of seCards) {
        const hasPosRow = !!eventPosToken('settlement', card.event);
        card.visibleLines.forEach((l, i) => {
          seLineCoord.set(l.lineId, {
            nodeId: card.nodeId,
            cardY: card.y,
            visibleIndex: i,
            hasPosRow,
            // #833 / #889 — settlement-side never carries either
            // subtitle (both are delivery-only render gates).
            hasBestellRow: false,
            hasLieferterminRow: false,
          });
        });
      }
      const deLineCoord = new Map<string, LineCoord>();
      for (const card of deCards) {
        const hasPosRow = !!eventPosToken('delivery', card.event);
        const hasBestellRow =
          typeof card.event.bestell_number === 'string' &&
          (card.event.bestell_number as string).length > 0;
        // #889 — same render gate ``lieferterminChipValue`` uses inside
        // ``buildLineRowNode``. Reading the helper instead of inlining
        // the equality check keeps the LineCoord flag in lockstep with
        // the rendered chip — any future tweak to the suppression rule
        // (e.g. legacy-row handling) flows through automatically.
        const hasLieferterminRow =
          !!lieferterminChipValue('delivery', card.event);
        card.visibleLines.forEach((l, i) => {
          deLineCoord.set(l.lineId, {
            nodeId: card.nodeId,
            cardY: card.y,
            visibleIndex: i,
            hasPosRow,
            hasBestellRow,
            hasLieferterminRow,
          });
        });
      }

      // Real line-level matches always render an edge. Stubs are reserved
      // for *explicit* defects (quantity_mismatch); plain "no candidate"
      // unmatched lines stay quiet — the absence of an edge is the signal.
      // #1291 — one edge per pair (N:1 fan-out from a single LS spot);
      // the dashed stub fires only when ZERO edges could be drawn.
      for (const card of deCards) {
        for (const dl of card.visibleLines) {
          const fanOut = buildDeliveryLineFanOutEdges({
            dl,
            sourceNodeId: card.nodeId,
            seLineCoord,
          });
          edges.push(...fanOut.edges);
          if (fanOut.needsStub) {
            const coord = deLineCoord.get(dl.lineId);
            if (!coord) continue;
            const stubId = `stub_de_${dl.lineId}`;
            nodes.push({
              id: stubId,
              type: 'stubMarker',
              position: {
                x: COL_MID - 8,
                y:
                  lineYCenter(
                    coord.cardY,
                    coord.visibleIndex,
                    coord.hasPosRow,
                    coord.hasBestellRow,
                    // #889 — include the Liefertermin reservation so the
                    // dashed quantity_mismatch stub points at the actual
                    // handle on LS cards that render the chip.
                    coord.hasLieferterminRow,
                  ) - 8,
              },
              data: { orphanType: 'rejected' },
            });
            edges.push({
              id: `e_stub_${dl.lineId}`,
              source: card.nodeId,
              sourceHandle: dl.lineId,
              target: stubId,
              type: 'straight',
              style: { stroke: '#e5e7eb', strokeWidth: 1.5, strokeDasharray: '5 3' },
            });
          }
        }
      }

      // Settlement-side stubs follow the same rule: only true defects
      // surface; plain unmatched goods (the common "rules engine hasn't
      // run yet" state) stay quiet.
      for (const card of seCards) {
        for (const sl of card.visibleLines) {
          if (!sl.matchedLineId && sl.matchStatus === 'quantity_mismatch') {
            const coord = seLineCoord.get(sl.lineId);
            if (!coord) continue;
            const stubId = `stub_se_${sl.lineId}`;
            nodes.push({
              id: stubId,
              type: 'stubMarker',
              position: {
                x: COL_MID - 8,
                y:
                  lineYCenter(
                    coord.cardY,
                    coord.visibleIndex,
                    coord.hasPosRow,
                    coord.hasBestellRow,
                    // #889 — see paired-side stub above. GS-side
                    // coord.hasLieferterminRow is always false (the
                    // chip is delivery-only); kept explicit for parity.
                    coord.hasLieferterminRow,
                  ) - 8,
              },
              data: { orphanType: 'no_candidate' },
            });
            edges.push({
              id: `e_stub_${sl.lineId}`,
              source: stubId,
              target: card.nodeId,
              targetHandle: sl.lineId,
              type: 'straight',
              style: { stroke: '#e5e7eb', strokeWidth: 1.5, strokeDasharray: '5 3' },
            });
          }
        }
      }

      // #842 — advance past ``rowH`` (the precomputed envelope of the
      // tallest column). Pre-fix this loop took ``Math.max`` over each
      // column's running accumulator; post-fix the accumulators
      // reflect SHIFTED positions (shorter columns are pushed down by
      // their centering offset), and ``yOffset + rowH`` is the single
      // source of truth. ``deY`` / ``seY`` / ``bestellungY`` are still
      // used by the per-column build loops above; they're discarded
      // here intentionally.
      //
      // #917 — Abrechnung no longer participates in ``computeRowLayout``
      // (top-aligned to citing GS instead of row-centered). Fold the
      // actual Abrechnung-column depth back into the row envelope
      // here so a tall per-GS Abrechnung stack still gets a clean
      // ``GROUP_GAP`` to the next row. Same defensive pattern the
      // orphan_settlement branch already uses (``orphanRowH = max(...,
      // payoutEmission.bottomY - yOffset)``).
      const effectiveRowH = Math.max(
        layout.rowH,
        payoutEmission.bottomY - yOffset,
      );
      yOffset = yOffset + effectiveRowH + GROUP_GAP;
    }

    // ── Orphan section (banner + one row per orphan with a missing-side stub) ─
    //
    // Each orphan owns its row. The empty side renders a dashed
    // "Lieferschein fehlt" / "Gutschrift fehlt" placeholder so the
    // reviewer can SEE the absent document instead of inferring it
    // from negative space. The old packed two-column layout placed an
    // unrelated LS orphan next to an unrelated GS orphan on the same
    // row, which read at a glance as if they were a pair — the user's
    // "misleading" complaint. Mixing LS+GS orphans into a single
    // date-desc list also gives one chronological scroll over the
    // whole unmatched band instead of two parallel ones.
    if (orphanLS.length || orphanGS.length) {
      if (pairedGroups.length > 0) yOffset += ORPHAN_BAND_GAP - GROUP_GAP;
      nodes.push({
        id: '__orphan_banner__',
        type: 'sectionBanner',
        position: { x: COL_LEFT, y: yOffset },
        data: { label: 'Ohne Zuordnung', width: COL_RIGHT + TILE_WIDTH - COL_LEFT },
        draggable: false,
        selectable: false,
      });
      yOffset += 28;

      interface OrphanRow {
        group: MatchGroup;
        realSide: 'delivery' | 'settlement';
        sortDate: string;
      }
      const orphanRowsList: OrphanRow[] = [
        ...orphanLS.map((g) => ({
          group: g,
          realSide: 'delivery' as const,
          sortDate: readEventDate(g.deliveryEvent),
        })),
        ...orphanGS.map((g) => ({
          group: g,
          realSide: 'settlement' as const,
          sortDate: readEventDate(g.settlementEvent),
        })),
      ];
      orphanRowsList.sort((a, b) => byDateDesc(a.sortDate, b.sortDate));

      for (const row of orphanRowsList) {
        // #844 — open a fresh grid row per orphan group (mirrors the
        // paired-loop ``gridRowIndex`` opening).
        const gridRowIndex = gridRows.length;
        const gridRow = newRow();
        gridRows.push(gridRow);
        const allRealLines =
          row.realSide === 'delivery' ? row.group.deliveryLines : row.group.settlementLines;
        // Estimate the real tile's pre-measure baseline height by
        // counting only the lines LineRow will actually display as
        // goods rows and adding a footer-strip allowance when non-
        // goods (fee / packaging) lines exist. Used only for the
        // first frame before xyflow measures the real-side card —
        // #1123 then reads the real DOM height and the stub side
        // matches symmetrically.
        const visibleCount = visibleLineCount(allRealLines) || 1;
        const footerAllowance = hasFooterChips(allRealLines) ? FOOTER_STRIP_HEIGHT : 0;
        // #1123 — orphan rows match the stub side's height to the
        // measured real-side card so the row envelope (``rowH``)
        // stays correct as the LineRow content evolves (POS chip,
        // BestellNr subtitle, Liefertermin subtitle, wrapping footer,
        // any future conditional row). The pre-#1123 formula summed
        // a flag per conditional row and was the same brittle
        // architecture that drove the recurring stacking-bug in the
        // paired branch; the durable fix here is identical — read
        // the rendered height from the DOM, not from a formula.
        //
        // Real-side card id matches ``buildLineRowNode``'s primary
        // (cardIndex 0) shape — orphan rows always render exactly
        // one real card per side, so the id is the un-suffixed
        // ``${side}_${group.id}``.
        const realCardId =
          row.realSide === 'delivery'
            ? `de_${row.group.id}`
            : `se_${row.group.id}`;
        const baselineRowH = nodeHeight(visibleCount) + footerAllowance;
        const measuredRealH = measuredHeights.get(realCardId);
        const rowH =
          typeof measuredRealH === 'number' ? measuredRealH : baselineRowH;
        // #1160 — the missing-doc stubs in this band (generic
        // ``Gutschrift fehlt`` / ``Lieferschein fehlt`` and the typed
        // ``Gutschrift POS … (fehlt)`` token tiles) are FIXED-height,
        // no longer sized to the paired real card. The row's vertical
        // advance is reserved off ``orphanRowH`` below, growing to cover
        // whichever column (real card vs. fixed stub stack) is taller.
        // Tracks the row's tallest-column height for the ``yOffset``
        // advance. Defaults to ``rowH``; the orphan_settlement branch
        // may grow this to ``orphanLayout.rowH`` when its payout
        // stacks exceed the GS card (code-review P1 round 2 — pre-fix
        // the advance used ``rowH`` alone, so a row with ≥3 payout
        // tiles would overlap the next row).
        let orphanRowH = rowH;

        // Per-tile estimate for the "Gutschrift fehlt" stub. Only
        // meaningful when the missing side is the settlement (i.e.
        // group.status === 'orphan_delivery').
        const estimatedValue =
          row.group.status === 'orphan_delivery'
            ? estimateOrphanDeliveryValue(row.group, priceIndex)
            : null;

        // BESTELLUNG column on orphan rows is OUT OF SCOPE for #784 —
        // orphan_delivery's LS card sits on the left and has no
        // ``referenz-source`` handle, and orphan_settlement's GS lacks
        // the handle when ``referenceTo`` is empty. Wiring the edge
        // requires LineRow Handle surgery, which belongs in subtask 15
        // (#793 phase 1 — typed BESTELLUNG placeholders).
        if (row.realSide === 'delivery') {
          const de = buildLineRowNode(row.group, 'delivery', COL_LEFT, yOffset);
          if (de.node) {
            nodes.push(de.node);
            // #844 — orphan_delivery: the LS tile is the row's
            // ``lieferschein`` cell. ``gutschrift`` cell is filled
            // below — either by the generic ``miss_gs_*`` placeholder
            // (no POS tokens) or by the first POS-keyed placeholder
            // (typed missing-doc path).
            gridRow.lieferschein = tileRef(
              'lieferschein',
              gridRowIndex,
              de.nodeId,
              de.docId,
            );
          }
          // #793 phase 2 — orphan_delivery: when the LS's lines cite
          // GS-POS tokens the BFS couldn't resolve (the
          // ``expectedSettlementPosTokens`` list surfaced by the
          // materialiser), render ONE typed placeholder per token in
          // the GS column. Each placeholder header reads
          // ``Gutschrift POS <token> · (fehlt)`` so the operator
          // keeps the lead they need to hunt the missing GS down.
          // Per-line dotted POS arrows connect LS-line source handles
          // (lineId on the LS card) to the matching placeholder's
          // ``title-target`` handle. No tokens → fall back to the
          // generic single ``Gutschrift fehlt`` placeholder (phase 1
          // shape, the no-signal case from Acceptance §A.4).
          const tokens = row.group.expectedSettlementPosTokens;
          if (tokens.length === 0) {
            // #1119 case 1 — emit BOTH the generic missing-GS
            // placeholder AND a dashed LS → miss_gs reference edge,
            // mirroring the symmetric ``orphan_settlement`` GS →
            // miss_ls edge a few branches below. Pre-#1119 only the
            // node was pushed, leaving the placeholder floating next
            // to the LS with no visual link.
            const { node: missGsNode, edge: missGsEdge } =
              buildOrphanDeliveryMissGsPlaceholder(row.group, {
                lsNodeId: de.nodeId,
                x: COL_RIGHT,
                yOffset,
                tileWidth: TILE_WIDTH,
                moneyHint: missingMoneyHint(row.group, estimatedValue),
              });
            nodes.push(missGsNode);
            edges.push(missGsEdge);
            // #1160 — the generic stub is a fixed BESTELLUNG_TILE_HEIGHT
            // tile; fold it into the row envelope so a single-line LS
            // card (shorter than the stub) can't let the next row
            // overlap the placeholder bottom.
            orphanRowH = Math.max(orphanRowH, BESTELLUNG_TILE_HEIGHT);
            // #844 — placeholder occupies the GS column for keyboard
            // nav. ``docId: null`` keeps preview blank when the
            // operator lands here.
            gridRow.gutschrift = tileRef('gutschrift', gridRowIndex, missGsNode.id, null);
          } else {
            const {
              nodes: placeholderNodes,
              edges: placeholderEdges,
              stackHeight: posStackHeight,
            } = buildOrphanDeliveryPosPlaceholders(row.group, {
              lsNodeId: de.nodeId,
              x: COL_RIGHT,
              yOffset,
              tileWidth: TILE_WIDTH,
              moneyHint: missingMoneyHint(row.group, estimatedValue),
            });
            nodes.push(...placeholderNodes);
            edges.push(...placeholderEdges);
            // #1160 — the typed-token stub stack is fixed-height, so it
            // can be taller than the paired LS card (e.g. 3 tokens vs. a
            // 1-line LS). Grow the row envelope to the taller column so
            // the next orphan row never overlaps the token stack.
            orphanRowH = Math.max(orphanRowH, posStackHeight);
            // #844 — first POS-keyed placeholder takes the GS cell.
            // Stacked placeholders below it share the row but, per
            // the same first-only rule used for LS/GS stacks above,
            // only the topmost tile is reachable via arrow keys.
            if (placeholderNodes.length > 0) {
              gridRow.gutschrift = tileRef(
                'gutschrift',
                gridRowIndex,
                placeholderNodes[0].id,
                null,
              );
            }
          }
        } else {
          // #793 phase 1 — orphan_settlement: the LS is missing. The
          // GS letterhead's ``delivery_reference`` attribute carries
          // the LS number the GS cites (same attribute #781's Ref
          // chip reads on the live GS tile; see ``orphanRefNumber``
          // in ``buildLineRowNode``). Promote that number into the
          // placeholder header so the operator sees
          // ``Lieferschein #LF20250222 · (fehlt)`` instead of the
          // generic ``Lieferschein fehlt``.
          // #1137 — compact placeholder via the extracted builder
          // (``height: BESTELLUNG_TILE_HEIGHT``). Row advance stays
          // driven by ``orphanRowH`` (real GS card + payout stacks)
          // below, so shrinking this tile never overlaps the next row.
          const missLsNode = buildOrphanSettlementMissLsPlaceholderNode(row.group, {
            x: COL_LEFT,
            yOffset,
            tileWidth: TILE_WIDTH,
            moneyHint: missingMoneyHint(row.group, estimatedValue),
          });
          const missLsId = missLsNode.id;
          // Same source as the placeholder header (#793 phase 1 / #1137)
          // so the GS → miss_ls reference-edge gate below stays in
          // lockstep: the arrow is drawn iff the tile shows a number.
          const expectedDeliveryNumber = citedDeliveryReference(row.group);
          nodes.push(missLsNode);
          // #844 — placeholder LS occupies the ``lieferschein``
          // column for keyboard nav (docId: null → no preview).
          gridRow.lieferschein = tileRef(
            'lieferschein',
            gridRowIndex,
            missLsId,
            null,
          );
          const se = buildLineRowNode(row.group, 'settlement', COL_RIGHT, yOffset);
          if (se.node) {
            nodes.push(se.node);
            // #844 — real GS takes the ``gutschrift`` cell.
            gridRow.gutschrift = tileRef(
              'gutschrift',
              gridRowIndex,
              se.nodeId,
              se.docId,
            );
          }
          // #793 phase 4 codex P1 — orphan_settlement rows can still
          // carry Abrechnung + Zahlungsavis references: the GS may
          // cite an abrechnungs_number even when its LS is absent,
          // and a Zahlungsavis can reference the GS's
          // credit_note_number independently. Emit the same payout
          // columns the paired loop does. The orphan-settlement GS
          // event entity lives on ``row.group.settlementEvent``
          // (single GS per orphan row); we wrap it as the single
          // source card.
          if (se.node && row.group.settlementEvent) {
            // #842 — center the Zahlungsavis tiles against the GS
            // row's midpoint. Pre-fix the payout tiles sat at the
            // top of the row while the much-taller GS extended down
            // — the canary "Wiesenhof April 2026 Ohne Zuordnung"
            // reading. Row height for an orphan_settlement row is
            // the GS's ``rowH`` (LS placeholder is sized to match,
            // so the lsgs envelope equals rowH).
            //
            // #917 — Abrechnung column no longer participates in
            // row centering; each Abrechnung tile top-aligns to its
            // citing GS (a single synthetic source card in this
            // branch, anchored at ``yOffset``). The post-emission
            // ``orphanRowH = max(orphanRowH, payoutEmission.bottomY −
            // yOffset)`` fold below folds the Abrechnung-stack
            // depth back into the row envelope.
            // #1123 — Zahlungsavis tiles are fixed-height by design
            // (no conditional rows); ``buildPayoutColumns`` positions
            // them by the fixed constant, so the envelope MUST use
            // the same fixed baseline — see the paired-loop comments
            // for the codex round 1 P2 reasoning.
            const orphanAvisH = stackHeight({
              cardIds: row.group.payoutAdvices.map(
                (_, idx) => `payout_advice_${row.group.id}_${idx}`,
              ),
              baselineHeights: row.group.payoutAdvices.map(
                () => BESTELLUNG_TILE_HEIGHT,
              ),
              measuredHeights: EMPTY_MEASURED_HEIGHTS,
              stackGap: STACK_GAP,
            });
            const orphanLayout = computeRowLayout({
              lsgs: rowH,
              avis: orphanAvisH,
            });
            orphanRowH = Math.max(orphanRowH, orphanLayout.rowH);
            const payoutEmission = buildPayoutColumns(row.group, {
              sourceCards: [
                {
                  nodeId: se.nodeId,
                  event: row.group.settlementEvent,
                  // #917 — the synthetic single GS card in the
                  // orphan_settlement branch sits at the row top;
                  // its Abrechnung tiles stack downward from
                  // ``yOffset``.
                  y: yOffset,
                },
              ],
              yOffset,
              avisShift: orphanLayout.shifts.avis,
            });
            nodes.push(...payoutEmission.nodes);
            edges.push(...payoutEmission.edges);
            // #844 — register payout-column tiles into this orphan
            // row's grid entry (same shape as the paired-loop pass).
            for (const pn of payoutEmission.nodes) {
              const dataMissingSide = (pn.data as { missingSide?: unknown } | undefined)?.missingSide;
              if (
                pn.type === 'payoutStatement' ||
                (pn.type === 'missingDoc' && dataMissingSide === 'abrechnung')
              ) {
                if (gridRow.abrechnung == null) {
                  gridRow.abrechnung = tileRef('abrechnung', gridRowIndex, pn.id, null);
                }
              } else if (pn.type === 'payoutAdvice') {
                if (gridRow.zahlungsavis == null) {
                  gridRow.zahlungsavis = tileRef('zahlungsavis', gridRowIndex, pn.id, null);
                }
              }
            }
            // #827 — defensively fold the actual payout-column bottom
            // into ``orphanRowH``. ``orphanLayout.rowH`` already
            // estimates the column footprint via ``stackHeight``
            // (which omits the trailing STACK_GAP after the last
            // tile), but ``buildPayoutColumns`` accumulates
            // ``BESTELLUNG_TILE_HEIGHT + STACK_GAP`` per tile and
            // therefore reports a ``bottomY`` one STACK_GAP past
            // the estimate. Without this fold the next orphan row
            // could overlap the bottom of the payout column on rows
            // where the payout stack is the tallest column.
            orphanRowH = Math.max(orphanRowH, payoutEmission.bottomY - yOffset);
          }
          // #793 phase 1 — draw the GS → LS-placeholder arrow when
          // the GS named a specific LS. Reuses the same handle ids,
          // stroke style, dash pattern, and marker the resolved
          // GS-Referenz edge uses (see the paired-render edge.push
          // around line 880) so the visual is identical to the
          // matched path. ``coLinear: true`` because the placeholder
          // sits at exactly the same y as the GS (no card stacking
          // on the LS side — the placeholder IS the LS card). No
          // expected number → no arrow (Acceptance §A.4).
          if (expectedDeliveryNumber && se.nodeId) {
            // #843 — same shared primitive as the paired-render REF
            // edge and the LS→GS POS-placeholder edge. ``coLinear:
            // true`` because the placeholder IS the LS card (sits at
            // exactly the same y as the GS).
            edges.push(
              buildDashedReferenceEdge({
                id: `ref_miss_${row.group.id}`,
                source: se.nodeId,
                sourceHandle: 'referenz-source',
                target: `miss_ls_${row.group.id}`,
                targetHandle: 'title-target',
                coLinear: true,
              }),
            );
          }
        }
        yOffset += orphanRowH + GROUP_GAP;
      }
    }

    // #785 — yOffset at this point is the bottom of the last row's
    // content + the trailing GROUP_GAP. That's the canvas height the
    // VerticalScrollbar sizes its thumb off. ``Math.max(yOffset, 1)``
    // guards the empty-canvas case (no groups → 1px height → scrollbar
    // hidden by its own contentHeight gate).
    const contentHeight = Math.max(yOffset, 1);
    const grid: NavGrid = { rows: gridRows };
    return { nodes, edges, contentHeight, grid, cardSideMap };
  }, [
    groups,
    allGroups,
    selectedDocId,
    resolvedOverrides,
    onMarkResolved,
    onUnmarkResolved,
    suppressOverrides,
    onSuppress,
    onUnsuppress,
    flagOverrides,
    flagCommentOverrides,
    onFlag,
    onUnflag,
    measuredHeights,
  ]);

  // #1123 — controlled-nodes wiring per the xyflow v12 layout-from-
  // measurement pattern. Initial value seeds from the first layout
  // pass (baseline positions, pre-ResizeObserver); the effect below
  // mirrors subsequent ``layout.nodes`` references whenever any
  // layout input changes — most importantly, after xyflow's per-
  // node ResizeObserver populates ``measured.height`` and the
  // ``useMeasuredHeights`` consumer triggers a re-derivation.
  //
  // ``onNodesChange`` is forwarded to ``<ReactFlow>`` so xyflow can
  // own internal state transitions (selection, dimensions, etc.);
  // our effect remains the source of truth for ``position`` because
  // every render that changes a layout input replays the layout
  // function from scratch.
  //
  // **Measurement-preservation**: the layout function produces fresh
  // node objects without a ``measured`` field, but xyflow's per-node
  // adoption logic (``adoptUserNodes``) reads ``userNode.measured``
  // when rebuilding the internal node — passing nodes without it
  // would reset ``nodeLookup`` measurements to ``undefined``,
  // triggering a wasted re-measure cycle. Merge the existing state
  // nodes' ``measured`` field into the replayed layout nodes so the
  // adoption step preserves measurement across layout replays.
  // (Codex P1.)
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>(layout.nodes);
  useEffect(() => {
    setNodes((prev) => {
      const prevMeasured = new Map<string, Node['measured']>();
      for (const n of prev) {
        if (n.measured) prevMeasured.set(n.id, n.measured);
      }
      return layout.nodes.map((n) => {
        const m = prevMeasured.get(n.id);
        return m ? { ...n, measured: m } : n;
      });
    });
  }, [layout.nodes, setNodes]);
  const { edges, contentHeight, grid } = layout;

  // #844 — Focused tile for keyboard navigation. ``null`` on initial
  // render (and after Escape); the first arrow press resolves to the
  // top-left populated cell via ``moveSelection(grid, null, key)``.
  // Kept separate from the parent-owned ``selectedDocId`` so a tile
  // can be focused without driving the preview panel (transient
  // state during a multi-key traversal — only the LAST arrow press
  // calls the parent's ``onNodeClick``) and so Escape clears focus
  // locally before the parent reacts.
  const [focused, setFocused] = useState<NavSelection>(null);

  // Keep ``focused`` consistent with the parent's ``selectedDocId``
  // when the operator clicks a tile: the click flows through
  // ``onNodeClick`` to ``selectedDocId`` (parent state), and the
  // next arrow press should originate from the clicked tile, not
  // from wherever the keyboard cursor used to be. Sync by docId
  // because the parent doesn't know about column / rowIndex.
  //
  // Read ``focused`` through a ref so the sync runs ONLY on external
  // ``selectedDocId`` changes (click path) and on grid recomputes
  // (vanished-cell recovery). Without the ref, arrow-nav onto a
  // ``docId: null`` tile (Bestellung / payout / missing-doc) would
  // leave ``selectedDocId`` stale (handleKey skips ``onNodeClick``
  // for null-doc cells by design), the next render would re-run this
  // effect with the new ``focused`` in deps, see ``focused.docId !==
  // selectedDocId``, scan, and snap ``focused`` BACK to the old
  // ``selectedDocId``'s cell — the user's keyboard cursor would
  // bounce. Codex round 1 P2.
  const focusedRef = useRef<NavSelection>(focused);
  useEffect(() => {
    focusedRef.current = focused;
  }, [focused]);
  useEffect(() => {
    if (selectedDocId == null) return;
    if (focusedRef.current?.docId === selectedDocId) return;
    for (const row of grid.rows) {
      for (const column of COLUMN_ORDER) {
        const cell = row[column];
        if (cell && cell.docId === selectedDocId) {
          setFocused(cell);
          return;
        }
      }
    }
  }, [selectedDocId, grid]);

  // Re-derive nodes with an ``isFocused`` data flag merged in for
  // whichever tile the keyboard reducer currently points at. Cheap
  // O(n) overlay so the parent useMemo doesn't need to re-run on
  // focus changes (which would also re-run when ``selectedDocId``
  // changes — adding a second render pass per click).
  const focusedNodeId = focused?.nodeId ?? null;
  const decoratedNodes = useMemo(() => {
    if (!focusedNodeId) return nodes;
    return nodes.map((n) =>
      n.id === focusedNodeId
        ? { ...n, data: { ...(n.data as Record<string, unknown>), isFocused: true } }
        : n,
    );
  }, [nodes, focusedNodeId]);

  // Global keydown handler. Attached to ``document`` so the operator
  // can navigate without first clicking the canvas (the more common
  // entry path is from the FilterBar dropdown; requiring a click
  // first would defeat the keyboard-first ergonomics).
  //
  // Guards (mirror task spec):
  //   - skip when typing in INPUT / TEXTAREA / contenteditable so the
  //     FilterBar search field + any in-tile inputs keep their native
  //     behaviour;
  //   - skip when Cmd/Ctrl is held so browser shortcuts (Ctrl+L
  //     address bar, Cmd+Down end-of-page, etc.) keep working.
  const onNodeClickRef = useRef(onNodeClick);
  useEffect(() => {
    onNodeClickRef.current = onNodeClick;
  }, [onNodeClick]);

  const handleKey = useCallback(
    (key: NavKey) => {
      setFocused((prev) => {
        const next = moveSelection(grid, prev, key);
        // Drive the preview panel only when the focused tile has a
        // real source doc id. Payout tiles + missing-doc placeholders
        // (and provenance-less / sparse Bestellung tiles) carry
        // ``docId: null``; a Bestellung with order-doc provenance
        // (#1812) carries its ``source_doc_id`` and DOES open a
        // preview on focus — matching its mouse click. Escape clears.
        if (key === 'Escape') {
          onNodeClickRef.current?.({ documentId: null });
        } else if (next && next.docId) {
          onNodeClickRef.current?.({ documentId: next.docId });
        }
        return next;
      });
    },
    [grid],
  );

  useEffect(() => {
    const isNavKey = (k: string): k is NavKey =>
      k === 'ArrowUp' ||
      k === 'ArrowDown' ||
      k === 'ArrowLeft' ||
      k === 'ArrowRight' ||
      k === 'Escape';
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isNavKey(e.key)) return;
      // Don't fight browser shortcuts (Cmd/Ctrl + Arrow scrolls, etc.).
      if (e.metaKey || e.ctrlKey) return;
      // Don't steal arrow keys from text editing surfaces.
      const t = e.target;
      if (t instanceof HTMLInputElement) return;
      if (t instanceof HTMLTextAreaElement) return;
      if (t instanceof HTMLElement && t.isContentEditable) return;
      e.preventDefault();
      handleKey(e.key);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handleKey]);

  // #785 — measure the container's rendered height so the
  // VerticalScrollbar can size its thumb (viewportHeight / contentHeight).
  // ResizeObserver fires on window resize, sidebar toggles, etc., so the
  // thumb stays in sync without polling.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [viewportHeight, setViewportHeight] = useState(0);
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    setViewportHeight(node.clientHeight);
    if (typeof ResizeObserver === 'undefined') return;
    const obs = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setViewportHeight(entry.contentRect.height);
      }
    });
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  // #785 — subscribe to React Flow's viewport so the scrollbar thumb
  // mirrors mousewheel-driven pans (today's default vertical pan
  // behavior keeps working — we just paint a thumb that follows).
  const viewport = useViewport();
  const reactFlow = useReactFlow();

  // #1051 — derived-edges ⊎ optimistic-edges. Dedupe by edge id so a
  // refetch landing the "real" derived edge (same id shape
  // ``e_{deliveryLineId}_{settlementLineId}``) replaces the
  // optimistic one without paint flicker.
  const renderedEdges = useMemo(() => {
    const merged = (() => {
      if (optimisticEdges.length === 0) return edges;
      const ids = new Set(edges.map((e) => e.id));
      const supplemental = optimisticEdges.filter((e) => !ids.has(e.id));
      return supplemental.length === 0 ? edges : [...edges, ...supplemental];
    })();
    // HOF-216 — stamp the selection bit back on. See ``handleEdgesChange``:
    // the edges array is DERIVED, so xyflow's own copy of ``selected`` is
    // thrown away on every render and the flag has to be re-applied here or
    // it survives exactly one frame.
    if (selectedEdgeIds.size === 0) return merged;
    return merged.map((e) => (selectedEdgeIds.has(e.id) ? { ...e, selected: true } : e));
  }, [edges, optimisticEdges, selectedEdgeIds]);

  /**
   * HOF-216 — edge selection was dead on this canvas, and that is why #1344
   * "did not take".
   *
   * React Flow's selection is a CHANGE, not a DOM state: clicking an edge
   * calls ``addSelectedEdges`` → ``triggerEdgeChanges``, which under a
   * CONTROLLED ``edges`` prop (``hasDefaultEdges === false``, because we pass
   * ``edges`` and not ``defaultEdges``) applies nothing and simply forwards to
   * ``onEdgesChange`` — which was not wired. So ``edge.selected`` was never
   * true: #1344's darker 3 px stroke could not paint, and the delete-key
   * handler, whose whole body is ``edges.filter(e => e.selected)``, had
   * nothing to delete. The issue reads the unlink as "keyboard-only"; it was
   * in fact unreachable, which is what the founder hit.
   *
   * Only ``select`` changes are honoured. Structure stays derived from
   * ``gutschrift_match_group`` — letting a ``remove`` reach local edge state
   * would let the canvas disagree with the twin, which is the one thing this
   * layout must never do (and it is exactly what ``onBeforeDelete`` vetoes).
   */
  const handleEdgesChange = useCallback((changes: EdgeChange<Edge>[]) => {
    const selections = changes.filter(
      (c): c is Extract<EdgeChange<Edge>, { type: 'select' }> => c.type === 'select',
    );
    if (selections.length === 0) return;
    setSelectedEdgeIds((prev) => {
      const next = new Set(prev);
      for (const change of selections) {
        if (change.selected) next.add(change.id);
        else next.delete(change.id);
      }
      // Reference-stable when nothing actually moved, so a deselect-everything
      // sweep over an already-empty set does not re-run the edges memo.
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) return prev;
      return next;
    });
  }, []);

  // #1120 — delegate to the buildHandleConnect factory (sibling module
  // ``handleConnect.ts``) so the pre-mutation bail toasts (formerly
  // silent ``return``s) can be unit-tested without mounting ReactFlow.
  // The factory captures lineSideMap / onManualLink / showToast /
  // setOptimisticEdges at construction time; useMemo keeps the
  // identity stable across renders so the ``onConnect`` prop doesn't
  // thrash xyflow's internal listener wiring.
  const handleConnect = useMemo(
    () =>
      buildHandleConnect({
        onManualLink,
        lineSideMap,
        // #3237 — same card map the onConnectEnd factory uses, so the
        // defence-in-depth backstop toasts don't regress to blunter copy.
        cardSideMap: layout.cardSideMap,
        showToast,
        setOptimisticEdges,
        // HOF-90 Part 2 — a reject that only toasts is a reject nobody can
        // count. See ``linkRejectionTelemetry.ts``.
        reportRejection: postLineLinkRejection,
      }),
    [lineSideMap, layout.cardSideMap, onManualLink, showToast],
  );

  // #1142 — the #1120 invalid-drop toast lived inside ``onConnect``, but
  // xyflow evaluates ``isValidConnection`` first and never fires
  // ``onConnect`` for an invalid drop, so the toast was unreachable.
  // ``onConnectEnd`` fires for every drop; the factory surfaces the
  // toast there when the drop landed on a handle the validator rejected.
  const handleConnectEnd = useMemo(
    () =>
      buildHandleConnectEnd({
        onManualLink,
        showToast,
        lineSideMap,
        // #3237 — lets a header-to-header drop between two cards of ONE
        // match group surface copy that names the Waren-line spots
        // instead of the blunt "nicht den Belegkopf …" reject.
        cardSideMap: layout.cardSideMap,
        // HOF-90 Part 2 — this is the handler the real canvas path reaches,
        // so it is where the fleet's operator-friction signal comes from.
        reportRejection: postLineLinkRejection,
      }),
    [onManualLink, showToast, lineSideMap, layout.cardSideMap],
  );

  // xyflow v12's ``isValidConnection`` may receive either a partially-
  // populated drag ``Connection`` (sourceHandle: string | null) OR an
  // already-built ``Edge`` (sourceHandle?: string | null | undefined).
  // The helper accepts both shapes via its narrow ``ConnectionLike``
  // type; coerce undefined → null at the boundary so the predicate is
  // exhaustively typed.
  const validateConnection = useCallback<IsValidConnection<Edge>>(
    (edgeOrConnection) => {
      return isValidLineConnection(
        {
          source: edgeOrConnection.source ?? null,
          target: edgeOrConnection.target ?? null,
          sourceHandle: edgeOrConnection.sourceHandle ?? null,
          targetHandle: edgeOrConnection.targetHandle ?? null,
        },
        lineSideMap,
      );
    },
    [lineSideMap],
  );

  // HOF-216 — the visible unlink affordance.
  //
  // Pre-HOF-216 this block WAS the unlink: ``onEdgesDelete`` normalised the
  // deleted edge and posted straight to the line-link endpoint, reachable only
  // by selecting the edge and pressing Backspace/Delete. Nothing in the app
  // said so — a grep of ``frontend/src`` found those key names in code comments
  // only — so on a trackpad or touch device the unlink did not exist, and on
  // 2026-08-31 the founder rediscovered it as missing while reconciling
  // Wiesenhof. #1344 had already found the same thing once and fixed the
  // *selection* rather than the *trigger*.
  //
  // Three triggers now converge on ONE gated handler (``edgeUnlinkHandlers``):
  // the on-edge control, the right-click menu, and the unchanged shortcut. The
  // gate is a confirmation naming both lines and both quantities — this is a
  // twin write on a paying tenant's ``gutschrift_match_group`` under
  // impersonation, and making an existing mutation reachable raises its call
  // rate, so the confirmation IS the mitigation.
  const [pendingUnlink, setPendingUnlink] = useState<PendingUnlink | null>(null);
  // Container-relative position of the open right-click menu, with the edge it
  // was opened on. ``null`` = closed.
  const [edgeMenu, setEdgeMenu] = useState<{ x: number; y: number; edge: Edge } | null>(null);

  // ``lineId → {productName, quantity, unit}`` for the confirmation copy.
  // Deliberately a second map rather than a widened ``LineSideEntry``: that
  // shape is the connection validators' contract, pinned by
  // ``lineLinkValidation.test.ts``, and it has no business carrying copy.
  const lineDescriptors = useMemo(() => buildLineDescriptorMap(groups), [groups]);

  // Pairs whose unlink is in the air. A ref, not state: it must survive the
  // memo rebuild every ``groups`` refetch causes, and nothing renders from it.
  const inFlightUnlinks = useRef<Set<string>>(new Set());

  const unlinkHandlers = useMemo(
    () =>
      buildEdgeUnlinkHandlers({
        lineSideMap,
        lineDescriptors,
        onManualUnlink,
        showToast,
        openConfirm: setPendingUnlink,
        reportTelemetry: postLineUnlinkTelemetry,
        inFlight: inFlightUnlinks.current,
        // HOF-219 — keeps ``inert_edge`` meaning "nothing severs this at any
        // layer". The document edge is severable by mouse now, just not by
        // this key (AC 14).
        hasDocumentUnlink: (edge) => !!onDocumentUnlink && isDocumentUnlinkEdge(edge),
      }),
    [lineSideMap, lineDescriptors, onManualUnlink, onDocumentUnlink, showToast],
  );

  // HOF-219 — the DOCUMENT-level unlink, deliberately its own state, its own
  // in-flight set and its own handler factory. The two layers share the modal
  // component and the telemetry lane, and nothing else: their payloads are
  // different shapes, their endpoints are different routes, and merging them
  // would mean a runtime discriminator in every place the type system already
  // knows which one it is holding.
  const [pendingDocumentUnlink, setPendingDocumentUnlink] = useState<PendingDocumentUnlink | null>(null);
  const inFlightDocumentUnlinks = useRef<Set<string>>(new Set());
  const documentUnlinkHandlers = useMemo(
    () =>
      buildDocumentUnlinkHandlers({
        onDocumentUnlink,
        showToast,
        openConfirm: setPendingDocumentUnlink,
        reportTelemetry: postLineUnlinkTelemetry,
        inFlight: inFlightDocumentUnlinks.current,
      }),
    [onDocumentUnlink, showToast],
  );
  const documentEdgeActions = useMemo(
    () => (onDocumentUnlink ? { requestDocumentUnlink: documentUnlinkHandlers.requestDocumentUnlink } : null),
    [onDocumentUnlink, documentUnlinkHandlers],
  );

  // Handed to ``MatchedLineEdge`` through context, because ``edgeTypes`` is a
  // module constant (it must keep its identity or every edge remounts each
  // render) and so cannot take props. ``null`` without ``onManualUnlink`` is
  // what makes a Storybook / standalone render show no control (AC 7).
  const edgeActions = useMemo(
    () => (onManualUnlink ? { requestUnlink: unlinkHandlers.requestUnlink } : null),
    [onManualUnlink, unlinkHandlers],
  );

  const handleEdgeContextMenu = useCallback(
    (event: ReactMouseEvent, edge: Edge) => {
      // Two predicates, one menu. ``isManualLineEdge`` still decides the
      // line↔line unlink exactly as it did (HOF-216 AC 8, unchanged);
      // ``isDocumentUnlinkEdge`` decides the document one (HOF-219). An
      // Abrechnung edge, a Zahlungsavis edge or a stub matches neither and
      // keeps the browser's own menu, because nothing severs those at any
      // layer — offering a control there would promise a capability.
      const line = !!onManualUnlink && isManualLineEdge(edge);
      const document = !!onDocumentUnlink && isDocumentUnlinkEdge(edge);
      if (!line && !document) return;
      event.preventDefault();
      const rect = containerRef.current?.getBoundingClientRect();
      setEdgeMenu({
        x: event.clientX - (rect?.left ?? 0),
        y: event.clientY - (rect?.top ?? 0),
        edge,
      });
    },
    [onManualUnlink, onDocumentUnlink],
  );

  const closeEdgeMenu = useCallback(() => setEdgeMenu(null), []);

  // A confirmation must not outlive the edge it is about (codex second pass,
  // P1). It holds a PAIR, not a revision — the `line-link` endpoint has no
  // revision token — and the canvas deliberately keeps the edge on screen until
  // the refetch. So if another actor unlinks that pair while the dialog is
  // open, the derived edges lose it and the dialog is now asking about a view
  // that has moved; dismiss it rather than let a confirm land on the new state.
  // ``edges`` (derived), not ``renderedEdges``: an optimistic edge is our own
  // in-flight guess and says nothing about what the twin currently holds.
  useEffect(() => {
    if (pendingUnlink && !edges.some((e) => e.id === pendingUnlink.edgeId)) {
      setPendingUnlink(null);
    }
    // HOF-219 — same rule, same reason, one layer up. The document-link
    // endpoint carries no revision token either, and the confirmation holds a
    // PAIR: if a re-materialise removes that edge while the dialog is open, the
    // dialog is asking about a view that has moved, so dismiss it rather than
    // let a confirm land on the new state.
    if (pendingDocumentUnlink && !edges.some((e) => e.id === pendingDocumentUnlink.edgeId)) {
      setPendingDocumentUnlink(null);
    }
    if (edgeMenu && !edges.some((e) => e.id === edgeMenu.edge.id)) {
      setEdgeMenu(null);
    }
  }, [edges, pendingUnlink, pendingDocumentUnlink, edgeMenu]);

  const handleConfirmUnlink = useCallback(() => {
    const pending = pendingUnlink;
    if (!pending) return;
    // Close first: the modal must not sit over the canvas while the write is
    // in flight, and a second click on a still-mounted confirm button would
    // otherwise be a second POST.
    setPendingUnlink(null);
    void unlinkHandlers.confirmUnlink(pending);
  }, [pendingUnlink, unlinkHandlers]);

  const handleConfirmDocumentUnlink = useCallback(() => {
    const pending = pendingDocumentUnlink;
    if (!pending) return;
    // Close first, for the same reason as the line-level confirm: the modal
    // must not sit over the canvas while the write is in flight, and a second
    // click on a still-mounted confirm button would be a second POST.
    setPendingDocumentUnlink(null);
    void documentUnlinkHandlers.confirmDocumentUnlink(pending);
  }, [pendingDocumentUnlink, documentUnlinkHandlers]);

  return (
    <div
      ref={containerRef}
      style={{ flex: 1, minHeight: 0, position: 'relative' }}
      data-testid="match-graph"
    >
      <MatchedLineEdgeActionsContext.Provider value={edgeActions}>
      <DocumentRefEdgeActionsContext.Provider value={documentEdgeActions}>
      <ReactFlow
        // Remount when the visible group set changes so the viewport
        // always resets to the top of the canvas — without this,
        // switching months leaves the user scrolled half-way down the
        // previous list. #915 dropped the legacy ``anyBestellung`` bit
        // (Bestellung is no longer in its own off-canvas column, so
        // the bit's "re-apply the leftward pan on hydration" job is
        // moot).
        key={computeMatchGraphRemountKey(groups.map((g) => g.id))}
        nodes={decoratedNodes}
        edges={renderedEdges}
        // #1123 — controlled ``useNodesState`` wires xyflow's
        // internal change events (selection / dimensions / etc.)
        // back into our nodes state. Our ``setNodes(layout.nodes)``
        // effect remains the source of truth for ``position``
        // every time a layout input changes.
        onNodesChange={onNodesChange}
        // HOF-216 — required for edge SELECTION to apply at all under a
        // controlled ``edges`` prop; see ``handleEdgesChange``.
        onEdgesChange={handleEdgesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        // Anchor the canvas at (0, 0) zoom=1 so tile sizes stay
        // constant whether there's one paired group or twenty orphans,
        // and so tall content shows the top first (panning down
        // reveals the rest) instead of being vertically centre-cropped.
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        proOptions={{ hideAttribution: true }}
        minZoom={0.3}
        nodesDraggable={false}
        // #1051 — drag-to-connect is gated on the manual-link callback
        // being wired up. Storybook / standalone renders without a
        // callback keep the legacy no-interaction behaviour. With
        // ``connectionMode="loose"`` xyflow accepts source→source /
        // target→target so the operator can drag either direction
        // (LS→GS or GS→LS); ``isValidConnection`` enforces the
        // bipartite + same-group invariants.
        //
        // #3237 — ``connectionRadius`` is deliberately left at xyflow's
        // 20 px default rather than pinned to an explicit value. It was
        // considered as a way to make a header-height drop snap onto a
        // Waren line, and it cannot do that:
        //
        //  * ``getClosestHandle`` picks by DISTANCE only — it walks
        //    ``handleBounds`` and never consults connectability — so the
        //    chrome handle at the card edge always beats a goods spot
        //    ~100 px lower no matter how wide the radius gets. A radius
        //    big enough to reach a goods line from the header would have
        //    to span most of a card, which would also start snapping
        //    near-miss drops onto the WRONG goods line.
        //  * Shrinking it is worse than the status quo: a drop that
        //    resolves to no handle arrives at ``onConnectEnd`` with
        //    ``toHandle === null``, which the factory treats as a
        //    cancelled drag and deliberately leaves silent — so a
        //    tighter radius converts a wrong-but-visible toast into no
        //    feedback at all.
        //
        // The operator-facing fix is therefore the reason-specific
        // ``chrome_same_group`` copy, not a snap-radius tweak.
        nodesConnectable={!!onManualLink}
        connectionMode={ConnectionMode.Loose}
        // #1120 — bold green drag preview so operators can see what
        // they're connecting before the drop lands the optimistic
        // edge. Only relevant when drag-to-connect is wired up
        // (storybook / standalone renders skip it).
        connectionLineStyle={
          onManualLink ? MANUAL_LINE_LINK_CONNECTION_STYLE : undefined
        }
        isValidConnection={onManualLink ? validateConnection : undefined}
        onConnect={onManualLink ? handleConnect : undefined}
        onConnectEnd={onManualLink ? handleConnectEnd : undefined}
        // HOF-216 — the veto hook, not ``onEdgesDelete``. React Flow calls
        // ``onEdgesDelete`` only AFTER it has decided to remove the edge, so a
        // cancelled confirmation would leave the canvas already missing the
        // edge it was asking about (AC 4). ``onBeforeDelete`` returning
        // ``false`` removes nothing and calls no delete callback at all; on a
        // CONFIRMED unlink the edge leaves via the ``matchGroupKeys.all``
        // refetch, which is the same path that updates the group's status in
        // place (AC 5).
        onBeforeDelete={onManualUnlink ? unlinkHandlers.handleBeforeDelete : undefined}
        onEdgeContextMenu={onManualUnlink || onDocumentUnlink ? handleEdgeContextMenu : undefined}
        deleteKeyCode={onManualUnlink ? ['Backspace', 'Delete'] : null}
        edgesReconnectable={false}
        onNodeClick={(_, node) => {
          if (!onNodeClick) return;
          // #1812 — line-row tiles AND Bestellung tiles carrying the
          // order-doc provenance open a preview; everything else is inert.
          const target = resolveNodeClickTarget(node as { type?: string; data: Record<string, unknown> });
          if (target) onNodeClick(target);
        }}
        onPaneClick={() => {
          // HOF-216 — a pane click dismisses the edge context menu too.
          setEdgeMenu(null);
          // React Flow swallows the native click before our menus'
          // document.mousedown handler can see it. Dispatch a window
          // event so any open kebab dropdowns can close themselves
          // (see ``TileActionsMenu`` listener for the receive side).
          window.dispatchEvent(new Event(TILE_MENU_CLOSE_EVENT));
          // #844 — pane click clears the preview (parent #835 hook);
          // mirror that by clearing the keyboard focus ring so the
          // affordance stays in sync with the preview state.
          setFocused(null);
          onPaneClick?.();
        }}
      >
        <Controls showInteractive={false} position="bottom-right" />
      </ReactFlow>
      </DocumentRefEdgeActionsContext.Provider>
      </MatchedLineEdgeActionsContext.Provider>
      {edgeMenu && (
        <EdgeUnlinkMenu
          x={edgeMenu.x}
          y={edgeMenu.y}
          onUnlink={() => {
            // ONE menu action, routed by which layer the edge belongs to. The
            // label is the same German phrase at both layers by decision (spec
            // assumption 7); the confirmation body is what distinguishes them.
            if (isDocumentUnlinkEdge(edgeMenu.edge)) {
              documentUnlinkHandlers.requestDocumentUnlink(edgeMenu.edge, 'context_menu');
              return;
            }
            unlinkHandlers.requestUnlink(edgeMenu.edge, 'context_menu');
          }}
          onClose={closeEdgeMenu}
        />
      )}
      <ConfirmModal
        isOpen={!!pendingUnlink}
        title={UNLINK_CONFIRM_TITLE}
        message={pendingUnlink?.message ?? ''}
        confirmText={UNLINK_ACTION_LABEL}
        cancelText={UNLINK_CONFIRM_CANCEL}
        variant="danger"
        onConfirm={handleConfirmUnlink}
        onCancel={() => setPendingUnlink(null)}
      />
      <ConfirmModal
        isOpen={!!pendingDocumentUnlink}
        title={DOCUMENT_UNLINK_CONFIRM_TITLE}
        message={pendingDocumentUnlink?.message ?? ''}
        confirmText={UNLINK_ACTION_LABEL}
        cancelText={UNLINK_CONFIRM_CANCEL}
        variant="danger"
        onConfirm={handleConfirmDocumentUnlink}
        onCancel={() => setPendingDocumentUnlink(null)}
      />
      {/* #785 — overlay scrollbar. ``viewportHeight`` of 0 (first
          render before ResizeObserver fires) keeps the scrollbar
          hidden via its ``maxScroll <= 0`` gate, so we never paint a
          mis-sized thumb. */}
      <VerticalScrollbar
        contentHeight={contentHeight}
        viewportHeight={viewportHeight}
        viewportY={viewport.y}
        zoom={viewport.zoom}
        onScrollTo={(y) =>
          reactFlow.setViewport({ x: viewport.x, y, zoom: viewport.zoom })
        }
        trackHeight={viewportHeight}
      />
    </div>
  );
}
